import { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Stack,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  IconButton,
  Divider,
  CircularProgress,
  Pagination,
  FormControlLabel,
  Switch,
  InputAdornment
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import InventoryIcon from '@mui/icons-material/Inventory';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AddIcon from '@mui/icons-material/Add';
import AssignmentIcon from '@mui/icons-material/Assignment';
import StorageIcon from '@mui/icons-material/Storage';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LabelIcon from '@mui/icons-material/Label';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import Tooltip from '@mui/material/Tooltip';
import useObserveTripSheets from '../../hooks/useSubscribeTripsheet';
import { useCurrentUser, useDatastore } from '../../hooks';
import { useAlert } from '../../hooks';
import dayjs from 'dayjs';
import {
  addASNToSSCC,
  addASNToUnassignedSSCC,
  addSSCCToManifest,
  deleteASN,
  deleteManifest,
  deleteSSCC,
  markManifestCompleted,
  markSSCCCompleted,
  markUnassignedSSCCCompleted
} from '../../services/adminManifestActions';
import { ReceiptEventPackageProps, SSCCProps } from '../../datastore/ssccs';
import useObserveSsccs from '../../hooks/useSubscribeSsccs';
import { dateFormat } from '../../common/dateTimes';
import { SsccStatus } from '../../common';
import {
  AsnStatus,
  getTripDisplayStatus,
  ManifestStatus,
  mapSsccStatusToAsnStatus,
  mapTripLegStatusToSsccStatus,
  TripLegStatus
} from '../../common/status';
import { TripSheetProps } from '../../datastore/tripSheets';
import {
  MarkCompletedSSCCDialog,
  MarkCompletedManifestDialog,
  DeleteManifestDialog,
  DeleteSSCCDialog,
  DeleteASNDialog,
  AddSSCCToManifestDialog,
  AddASNToSSCCDialog,
  ConfirmationDialog,
  AddASNForUnassignedSSCCDialog,
  MarkCompletedUnassignedSSCCDialog
} from './dialogs';
import { SsccActionsDrawer } from './components/SsccActionsDrawer';
import {
  detectManifestOccurrences,
  detectSSCCOccurrences,
  detectASNOccurrences,
  computeUnassignedSSCCs,
  filterTripSheets,
  getTimeBasedOnStatus,
  transformManifest,
  buildAsnKey
} from './utils/manifestUtils';

import {
  ASNOccurrences,
  ASNToDelete,
  BustleDialogData,
  Manifest,
  ManifestOccurrences,
  SelectedManifest,
  SelectedManifestSSCC,
  SelectedUnassignedSSCC,
  SsccActionsContext,
  SsccActionsPayload,
  SSCCOccurrences
} from './utils/types';
import ReportingLogs from './components/ReportingLogs';
import { HEADER_HEIGHT } from './utils/values';

const PAGE_SIZE = 10;
const UNASSIGNED_SSCC_PAGE_SIZE = 24;
const PAGE_NUMBER_URL = 'page';
const TRIP_FILTER_URL = 'trip';
const TRIP_DESTINATION_FILTER_URL = 'tripDestination';
const MANIFEST_FILTER_URL = 'manifest';
const MANIFEST_TRAILER_ID_FILTER_URL = 'manifestTrailerId';
const SSCC_FILTER_URL = 'sscc';
const SSCC_NICKNAME_FILTER_URL = 'ssccNickname';
const ASN_FILTER_URL = 'asn';
const ASN_PO_NUMBER_FILTER_URL = 'asnPoNumber';
const DEPARTURE_DATE_FILTER_URL = 'departureDate';
const ARRIVAL_DATE_FILTER_URL = 'arrivalDate';
const HIDE_EMPTY_MANIFEST_TRIPS_URL = 'hideEmptyManifestTrips';
const SHOW_ONLY_MANIFEST_DUPLICATES_URL = 'showOnlyManifestDuplicates';
const SHOW_ONLY_SSCC_DUPLICATES_URL = 'showOnlySSCCDuplicates';
const SHOW_ONLY_ASN_DUPLICATES_URL = 'showOnlyASNDuplicates';
const SHOW_FILTERS_STORAGE_KEY = 'adminPanel.showFilters';
const SHOW_UNASSIGNED_SSCCS_STORAGE_KEY = 'adminPanel.showUnassignedSSCCs';

const getStoredBoolean = (key: string, fallback: boolean) => {
  const value = localStorage.getItem(key);

  if (value === null) {
    return fallback;
  }

  return value === 'true';
};

export default function AdminPanel() {
  const user = useCurrentUser();
  const alert = useAlert();
  const rawTripSheets = useObserveTripSheets();
  const [searchParams, setSearchParams] = useSearchParams();

  const [showHistory, setShowHistory] = useState(false);

  const [currentPage, setCurrentPage] = useState(Number(searchParams.get(PAGE_NUMBER_URL)) || 1);
  const [unassignedSSCCPage, setUnassignedSSCCPage] = useState(1);
  const [showFilters, setShowFilters] = useState(() => getStoredBoolean(SHOW_FILTERS_STORAGE_KEY, true));
  const [ssccActionsOpen, setSsccActionsOpen] = useState(false);
  const [ssccActionsPayload, setSsccActionsPayload] = useState<SsccActionsPayload | null>(null);

  const [tripSearch, setTripSearch] = useState(searchParams.get(TRIP_FILTER_URL) || '');
  const [legDestinationSearch, setLegDestinationSearch] = useState(searchParams.get(TRIP_DESTINATION_FILTER_URL) || '');
  const [selectedLegDepartureDate, setSelectedLegDepartureDate] = useState(
    searchParams.get(DEPARTURE_DATE_FILTER_URL) || ''
  );
  const [selectedLegArrivalDate, setSelectedLegArrivalDate] = useState(searchParams.get(ARRIVAL_DATE_FILTER_URL) || '');
  const [manifestSearch, setManifestSearch] = useState(searchParams.get(MANIFEST_FILTER_URL) || '');
  const [manifestTrailerIdSearch, setManifestTrailerIdSearch] = useState(
    searchParams.get(MANIFEST_TRAILER_ID_FILTER_URL) || ''
  );
  const [ssccSearch, setSsccSearch] = useState(searchParams.get(SSCC_FILTER_URL) || '');
  const [ssccNicknameSearch, setSsccNicknameSearch] = useState(searchParams.get(SSCC_NICKNAME_FILTER_URL) || '');
  const [asnSearch, setAsnSearch] = useState(searchParams.get(ASN_FILTER_URL) || '');
  const [asnPoNumberSearch, setAsnPoNumberSearch] = useState(searchParams.get(ASN_PO_NUMBER_FILTER_URL) || '');

  const [hideEmptyManifestTrips, setHideEmptyManifestTrips] = useState(
    searchParams.get(HIDE_EMPTY_MANIFEST_TRIPS_URL) === 'true'
  );
  const [showOnlyManifestDuplicates, setShowOnlyManifestDuplicates] = useState(
    searchParams.get(SHOW_ONLY_MANIFEST_DUPLICATES_URL) === 'true'
  );
  const [showOnlySSCCDuplicates, setShowOnlySSCCDuplicates] = useState(
    searchParams.get(SHOW_ONLY_SSCC_DUPLICATES_URL) === 'true'
  );
  const [showOnlyASNDuplicates, setShowOnlyASNDuplicates] = useState(
    searchParams.get(SHOW_ONLY_ASN_DUPLICATES_URL) === 'true'
  );
  const [showUnassignedSSCCs, setShowUnassignedSSCCs] = useState(() =>
    getStoredBoolean(SHOW_UNASSIGNED_SSCCS_STORAGE_KEY, true)
  );

  // 01 Add SSCC Dialog State
  const [openAddSSCCToManifestDialog, setOpenAddSSCCToManifestDialog] = useState(false);
  const [addSSCCToManifestData, setAddSSCCToManifestData] = useState<SelectedManifest | null>(null);
  const [addSSCCToManifestSelectedId, setAddSSCCToManifestSelectedId] = useState<string>('');
  const [addSSCCToManifestReason, setAddSSCCToManifestReason] = useState('');
  const [addSSCCToManifestWeight, setAddSSCCToManifestWeight] = useState<string>('0');
  const [addSSCCToManifestReceiveManifest, setAddSSCCToManifestReceiveManifest] = useState(true);

  // 02 Mark Completed Manifest Dialog State
  const [openCompleteManifestDialog, setOpenCompleteManifestDialog] = useState(false);
  const [completeManifestData, setCompleteManifestData] = useState<SelectedManifest | null>(null);
  const [completeManifestReason, setCompleteManifestReason] = useState('');

  // 03 Delete Manifest Dialog State
  const [openDeleteManifestDialog, setOpenDeleteManifestDialog] = useState(false);
  const [deleteManifestData, setDeleteManifestData] = useState<SelectedManifest | null>(null);
  const [deleteManifestReason, setDeleteManifestReason] = useState('');

  // 04 Add ASN Dialog State
  const [openAddASNToSSCCDialog, setOpenAddASNToSSCCDialog] = useState(false);
  const [addASNToSSCCData, setAddASNToSSCCData] = useState<SelectedManifestSSCC | null>(null);
  const [addASNToUnassignedSSCCData, setAddASNToUnassignedSSCCData] = useState<SelectedUnassignedSSCC | null>(null);
  const [addASNToSSCCSelectedId, setAddASNToSSCCSelectedId] = useState<string>('');
  const [addASNToSSCCReceiptEvent, setAddASNToSSCCReceiptEvent] = useState<string>('');
  const [addASNToSSCCReason, setAddASNToSSCCReason] = useState('');
  const [addASNToSSCCReceiveManifest, setAddASNToSSCCReceiveManifest] = useState(true);

  // 05 Mark Completed SSCC Dialog State
  const [openCompleteSSCCDialog, setOpenCompleteSSCCDialog] = useState(false);
  const [completeSSCCData, setCompleteSSCCData] = useState<SelectedManifestSSCC | null>(null);
  const [completeUnassignedSSCCData, setCompleteUnassignedSSCCData] = useState<SelectedUnassignedSSCC | null>(null);
  const [completeSSCCReason, setCompleteSSCCReason] = useState('');
  const [completeSSCCWeight, setCompleteSSCCWeight] = useState<string>('0');

  // 06 Delete SSCC Dialog State
  const [openDeleteSSCCDialog, setOpenDeleteSSCCDialog] = useState(false);
  const [deleteSSCCData, setDeleteSSCCData] = useState<SelectedManifestSSCC | null>(null);
  const [deleteSSCCReason, setDeleteSSCCReason] = useState('');

  // 07 Delete ASN Dialog State
  const [openDeleteASNDialog, setOpenDeleteASNDialog] = useState(false);
  const [deleteASNData, setDeleteASNData] = useState<ASNToDelete | null>(null);
  const [deleteASNReason, setDeleteASNReason] = useState('');
  const [deleteASNTransferSSCCId, setDeleteASNTransferSSCCId] = useState('');
  const [deleteASNTransferReceiptedEvent, setDeleteASNTransferReceiptedEvent] = useState('');

  // General Dialog State
  const [availableSSCCs, setAvailableSSCCs] = useState<SSCCProps[]>([]);
  const [openAdminBetaDialog, setOpenAdminBetaDialog] = useState(true);
  const [openBustleDialog, setOpenBustleDialog] = useState<BustleDialogData | null>(null);
  const [openCoupaDialog, setOpenCoupaDialog] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const contentScrollRef = useRef<HTMLDivElement | null>(null);

  const store = useDatastore();
  const ssccs = useObserveSsccs(0);

  // Get all available ASNs from all SSCCs
  const availableASNs = useMemo(() => {
    const asnMap = new Map<string, ReceiptEventPackageProps>();
    ssccs.forEach((sscc) => {
      sscc.receiptedEvents?.forEach((event) => {
        event.packages?.forEach((pkg) => {
          const isAvailableAsn = (pkg.status || '').toString().toLowerCase() === AsnStatus.available.toLowerCase();

          if (pkg.asn && !asnMap.has(pkg.asn) && !isAvailableAsn) {
            // Keep the first occurrence of each unique ASN
            asnMap.set(pkg.asn, pkg);
          }
        });
      });
    });

    return Array.from(asnMap.values()).sort((a, b) => (a.asn || '').localeCompare(b.asn || ''));
  }, [ssccs]);

  const availableASNsForAssignedSSCC = useMemo(() => {
    const currentSsccAsns = new Set(
      (addASNToSSCCData?.sscc?.sscc?.receiptedEvents || [])
        .flatMap((event) => event.packages || [])
        .map((pkg) => pkg.asn)
        .filter((asn): asn is string => Boolean(asn))
    );

    return availableASNs.filter((asn) => !asn.asn || !currentSsccAsns.has(asn.asn));
  }, [availableASNs, addASNToSSCCData?.sscc?.sscc?.receiptedEvents]);

  const availableASNsForUnassignedSSCC = useMemo(() => {
    const currentSsccAsns = new Set(
      (addASNToUnassignedSSCCData?.sscc?.receiptedEvents || [])
        .flatMap((event) => event.packages || [])
        .map((pkg) => pkg.asn)
        .filter((asn): asn is string => Boolean(asn))
    );

    return availableASNs.filter((asn) => !asn.asn || !currentSsccAsns.has(asn.asn));
  }, [availableASNs, addASNToUnassignedSSCCData?.sscc?.receiptedEvents]);

  const tripSheets = useMemo(() => {
    const fillUpSSCCDetails: TripSheetProps[] = (rawTripSheets || []).map((tripSheet) => {
      return {
        ...tripSheet,
        legs: tripSheet.legs.map((leg) => {
          return {
            ...leg,
            manifests: leg.manifests.map((manifest) => {
              return {
                ...manifest,
                ssccs: manifest.ssccs.map((sscc) => {
                  const foundSSCC: SSCCProps | undefined = ssccs.find((s) => s.id === sscc.ssccId);
                  return { ...sscc, ...foundSSCC };
                })
              };
            })
          };
        })
      };
    });

    return [...fillUpSSCCDetails].sort((a, b) =>
      String(b.reference ?? '').localeCompare(String(a.reference ?? ''), undefined, {
        numeric: true,
        sensitivity: 'base'
      })
    );
  }, [rawTripSheets, ssccs]);

  const ssccById = useMemo(() => new Map((ssccs || []).map((sscc) => [sscc.id, sscc])), [ssccs]);

  // Update URL when manifest search changes
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (currentPage !== 1) {
      params.set(PAGE_NUMBER_URL, String(currentPage));
    } else {
      params.delete(PAGE_NUMBER_URL);
    }
    if (hideEmptyManifestTrips) {
      params.set(HIDE_EMPTY_MANIFEST_TRIPS_URL, 'true');
    } else {
      params.delete(HIDE_EMPTY_MANIFEST_TRIPS_URL);
    }
    if (showOnlyManifestDuplicates) {
      params.set(SHOW_ONLY_MANIFEST_DUPLICATES_URL, 'true');
    } else {
      params.delete(SHOW_ONLY_MANIFEST_DUPLICATES_URL);
    }
    if (showOnlySSCCDuplicates) {
      params.set(SHOW_ONLY_SSCC_DUPLICATES_URL, 'true');
    } else {
      params.delete(SHOW_ONLY_SSCC_DUPLICATES_URL);
    }
    if (showOnlyASNDuplicates) {
      params.set(SHOW_ONLY_ASN_DUPLICATES_URL, 'true');
    } else {
      params.delete(SHOW_ONLY_ASN_DUPLICATES_URL);
    }
    if (tripSearch) {
      params.set(TRIP_FILTER_URL, tripSearch);
    } else {
      params.delete(TRIP_FILTER_URL);
    }
    if (legDestinationSearch) {
      params.set(TRIP_DESTINATION_FILTER_URL, legDestinationSearch);
    } else {
      params.delete(TRIP_DESTINATION_FILTER_URL);
    }
    if (selectedLegDepartureDate) {
      params.set(DEPARTURE_DATE_FILTER_URL, selectedLegDepartureDate);
    } else {
      params.delete(DEPARTURE_DATE_FILTER_URL);
    }
    if (selectedLegArrivalDate) {
      params.set(ARRIVAL_DATE_FILTER_URL, selectedLegArrivalDate);
    } else {
      params.delete(ARRIVAL_DATE_FILTER_URL);
    }
    if (manifestSearch) {
      params.set(MANIFEST_FILTER_URL, manifestSearch);
    } else {
      params.delete(MANIFEST_FILTER_URL);
    }
    if (manifestTrailerIdSearch) {
      params.set(MANIFEST_TRAILER_ID_FILTER_URL, manifestTrailerIdSearch);
    } else {
      params.delete(MANIFEST_TRAILER_ID_FILTER_URL);
    }
    if (ssccSearch) {
      params.set(SSCC_FILTER_URL, ssccSearch);
    } else {
      params.delete(SSCC_FILTER_URL);
    }
    if (ssccNicknameSearch) {
      params.set(SSCC_NICKNAME_FILTER_URL, ssccNicknameSearch);
    } else {
      params.delete(SSCC_NICKNAME_FILTER_URL);
    }
    if (asnSearch) {
      params.set(ASN_FILTER_URL, asnSearch);
    } else {
      params.delete(ASN_FILTER_URL);
    }
    if (asnPoNumberSearch) {
      params.set(ASN_PO_NUMBER_FILTER_URL, asnPoNumberSearch);
    } else {
      params.delete(ASN_PO_NUMBER_FILTER_URL);
    }
    setSearchParams(params, { replace: true });
  }, [
    manifestSearch,
    searchParams,
    tripSearch,
    legDestinationSearch,
    asnSearch,
    ssccSearch,
    setSearchParams,
    manifestTrailerIdSearch,
    ssccNicknameSearch,
    asnPoNumberSearch,
    selectedLegDepartureDate,
    selectedLegArrivalDate,
    hideEmptyManifestTrips,
    showOnlyManifestDuplicates,
    showOnlySSCCDuplicates,
    showOnlyASNDuplicates,
    currentPage
  ]);

  useEffect(() => {
    localStorage.setItem(SHOW_FILTERS_STORAGE_KEY, String(showFilters));
    localStorage.setItem(SHOW_UNASSIGNED_SSCCS_STORAGE_KEY, String(showUnassignedSSCCs));
  }, [showFilters, showUnassignedSSCCs]);

  // Detect duplicate manifests across all trips (must be before filters that use it)
  const manifestOccurrences: ManifestOccurrences = useMemo(() => detectManifestOccurrences(tripSheets), [tripSheets]);

  // Detect duplicate SSCCs across all manifests (must be before filters that use it)
  const ssccOccurrences: SSCCOccurrences = useMemo(() => detectSSCCOccurrences(tripSheets), [tripSheets]);

  // Detect duplicate ASNs across all manifests (must be before filters that use it)
  const asnOccurrences: ASNOccurrences = useMemo(
    () => detectASNOccurrences(tripSheets, ssccs || [], ssccOccurrences),
    [tripSheets, ssccs, ssccOccurrences]
  );

  // Compute unassigned SSCCs (SSCCs not in any manifest)
  const unassignedSSCCs = useMemo(() => {
    let result = computeUnassignedSSCCs(
      ssccs || [],
      ssccOccurrences,
      true,
      ssccSearch,
      ssccNicknameSearch,
      asnSearch,
      asnPoNumberSearch
    );
    if (showOnlyASNDuplicates) {
      result = result.filter((sscc) =>
        (sscc.receiptedEvents || []).some((event) =>
          (event.packages || []).some((pkg) => {
            const asnKey = buildAsnKey(pkg);
            return asnKey && (asnOccurrences.get(asnKey)?.length ?? 0) > 1;
          })
        )
      );
    }
    return result;
  }, [
    ssccs,
    ssccOccurrences,
    ssccSearch,
    ssccNicknameSearch,
    asnSearch,
    asnPoNumberSearch,
    showOnlyASNDuplicates,
    asnOccurrences
  ]);

  const unassignedSSCCTotalPages = useMemo(
    () => Math.max(1, Math.ceil(unassignedSSCCs.length / UNASSIGNED_SSCC_PAGE_SIZE)),
    [unassignedSSCCs.length]
  );

  const paginatedUnassignedSSCCs = useMemo(() => {
    const startIndex = (unassignedSSCCPage - 1) * UNASSIGNED_SSCC_PAGE_SIZE;
    return unassignedSSCCs.slice(startIndex, startIndex + UNASSIGNED_SSCC_PAGE_SIZE);
  }, [unassignedSSCCPage, unassignedSSCCs]);

  const paginatedUnassignedDuplicateAsnCountById = useMemo(() => {
    const duplicateCountById = new Map<string, number>();

    paginatedUnassignedSSCCs.forEach((sscc) => {
      const duplicateAsnCount = new Set(
        (sscc.receiptedEvents || [])
          .flatMap((event) => event.packages || [])
          .map((pkg) => buildAsnKey(pkg))
          .filter(
            (asnKey): asnKey is string => typeof asnKey === 'string' && (asnOccurrences.get(asnKey)?.length ?? 0) > 1
          )
      ).size;

      duplicateCountById.set(sscc.id, duplicateAsnCount);
    });

    return duplicateCountById;
  }, [paginatedUnassignedSSCCs, asnOccurrences]);

  useEffect(() => {
    setUnassignedSSCCPage(1);
  }, [showUnassignedSSCCs, ssccSearch, ssccNicknameSearch, asnSearch, asnPoNumberSearch]);

  useEffect(() => {
    if (unassignedSSCCPage > unassignedSSCCTotalPages) {
      setUnassignedSSCCPage(unassignedSSCCTotalPages);
    }
  }, [unassignedSSCCPage, unassignedSSCCTotalPages]);

  const eligibleTransferSSCCs = useMemo(() => {
    const assignedSSCCIds = new Set<string>();

    (tripSheets || []).forEach((trip) => {
      trip.legs.forEach((leg) => {
        leg.manifests.forEach((manifest) => {
          manifest.ssccs.forEach((sscc) => {
            if (!sscc?.ssccId) {
              return;
            }

            assignedSSCCIds.add(sscc.ssccId);
          });
        });
      });
    });

    return (ssccs || []).filter((sscc) => !assignedSSCCIds.has(sscc.id));
  }, [tripSheets, ssccs]);

  // Filter tripsheets based on search query and date
  const filteredTripSheets = useMemo(
    () =>
      filterTripSheets({
        tripSheets,
        tripSearch,
        legDestinationSearch,
        manifestSearch,
        manifestTrailerIdSearch,
        asnSearch,
        asnPoNumberSearch,
        ssccSearch,
        ssccNicknameSearch,
        selectedLegDepartureDate,
        selectedLegArrivalDate,
        hideEmptyManifestTrips,
        showOnlyManifestDuplicates,
        manifestOccurrences,
        showOnlySSCCDuplicates,
        ssccOccurrences,
        showOnlyASNDuplicates,
        asnOccurrences
      }),
    [
      tripSheets,
      tripSearch,
      legDestinationSearch,
      manifestSearch,
      manifestTrailerIdSearch,
      asnSearch,
      asnPoNumberSearch,
      ssccSearch,
      ssccNicknameSearch,
      selectedLegDepartureDate,
      selectedLegArrivalDate,
      hideEmptyManifestTrips,
      showOnlyManifestDuplicates,
      manifestOccurrences,
      showOnlySSCCDuplicates,
      ssccOccurrences,
      showOnlyASNDuplicates,
      asnOccurrences
    ]
  );

  const totalPages = useMemo(() => Math.ceil(filteredTripSheets.length / PAGE_SIZE), [filteredTripSheets.length]);

  const paginatedTripSheets = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    return filteredTripSheets.slice(startIndex, startIndex + PAGE_SIZE);
  }, [filteredTripSheets, currentPage]);

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
    contentScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleUnassignedSSCCPageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setUnassignedSSCCPage(value);
  };

  const handleHideEmptyManifestTripsToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setHideEmptyManifestTrips(event.target.checked);
    setCurrentPage(1);
  };

  const handleShowOnlyManifestDuplicatesToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowOnlyManifestDuplicates(event.target.checked);
    setCurrentPage(1);
  };

  const handleShowOnlySSCCDuplicatesToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowOnlySSCCDuplicates(event.target.checked);
    setCurrentPage(1);
  };

  const handleShowOnlyASNDuplicatesToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowOnlyASNDuplicates(event.target.checked);
    setCurrentPage(1);
  };

  const handleTripSearchChange = (value: string) => {
    setTripSearch(value);
    setCurrentPage(1);
  };

  const handleLegDestinationSearchChange = (value: string) => {
    setLegDestinationSearch(value);
    setCurrentPage(1);
  };

  const handleLegDepartureDateChange = (value: string) => {
    setSelectedLegDepartureDate(value);
    setCurrentPage(1);
  };

  const handleLegArrivalDateChange = (value: string) => {
    setSelectedLegArrivalDate(value);
    setCurrentPage(1);
  };

  const handleManifestSearchChange = (value: string) => {
    setManifestSearch(value);
    setCurrentPage(1);
  };

  const handleManifestTrailerIdSearchChange = (value: string) => {
    setManifestTrailerIdSearch(value);
    setCurrentPage(1);
  };

  const handleAsnSearchChange = (value: string) => {
    setAsnSearch(value);
    setCurrentPage(1);
  };

  const handleAsnPoNumberSearchChange = (value: string) => {
    setAsnPoNumberSearch(value);
    setCurrentPage(1);
  };

  const handleSsccSearchChange = (value: string) => {
    setSsccSearch(value);
    setCurrentPage(1);
  };

  const handleSsccNicknameSearchChange = (value: string) => {
    setSsccNicknameSearch(value);
    setCurrentPage(1);
  };

  const ssccActionsContext: SsccActionsContext | null = useMemo(() => {
    if (!ssccActionsPayload) {
      return null;
    }

    const trip = tripSheets.find((ts) => ts.id === ssccActionsPayload.tripId);
    if (!trip) {
      return null;
    }

    const leg = trip.legs.find((l) => l.id === ssccActionsPayload.legId);
    if (!leg) {
      return null;
    }

    const manifest = leg.manifests.find((m) => m.id === ssccActionsPayload.manifestId);
    if (!manifest) {
      return null;
    }

    const tripDisplayStatus = getTripDisplayStatus({
      manifestStatus: manifest.status,
      legStatus: leg.status
    });

    const transformedManifest = transformManifest({ manifest, trip, leg });
    const ssccEntry = transformedManifest.ssccsWithAsns.find((item) => item.ssccId === ssccActionsPayload.ssccId);

    return {
      trip,
      leg,
      manifest,
      transformedManifest,
      ssccEntry,
      tripDisplayStatus
    };
  }, [ssccActionsPayload, tripSheets]);

  // 00
  const handleCloseSsccActions = () => {
    setSsccActionsOpen(false);
    setSsccActionsPayload(null);
  };

  // 01A
  const handleOpenAddSSCCToManifestDialog = async ({
    manifest,
    tripDisplayStatus,
    ssccStatus,
    asnStatus,
    tripId,
    legId
  }: {
    manifest: Manifest;
    tripDisplayStatus: TripLegStatus;
    ssccStatus: string;
    asnStatus: string;
    tripId: string;
    legId: string;
    ssccId?: string;
  }) => {
    setAddSSCCToManifestData({ manifest, tripDisplayStatus, ssccStatus, asnStatus, tripId, legId });
    setOpenAddSSCCToManifestDialog(true);

    // Load available SSCCs that are not already in this manifest
    try {
      const allSSCCsOpen = await store.ssccs.getList('', undefined, 'open', true);
      const allSSCCsStaged = await store.ssccs.getList('', undefined, 'staged', true);
      const allSSCCs = [...(allSSCCsOpen || []), ...(allSSCCsStaged || [])].sort((a, b) =>
        String(b.id).localeCompare(String(a.id), undefined, { numeric: true, sensitivity: 'base' })
      );

      if (allSSCCs) {
        // Filter out SSCCs already in the manifest
        const filteredSSCCs = allSSCCs.filter(
          (sscc) => !manifest.ssccsWithAsns.map(({ ssccId }) => ssccId).includes(sscc.id)
        );

        // Fetch destination locations for each SSCC
        const ssccsWithLocations: SSCCProps[] = await Promise.all(
          filteredSSCCs.map(async (s) => {
            const destinationLocation = s.destinationLocationId
              ? await store.locations.getById(s.destinationLocationId)
              : undefined;
            return {
              ...s,
              destinationLocation
            };
          })
        );

        setAvailableSSCCs(ssccsWithLocations);
      }
    } catch (error) {
      console.error('Error loading SSCCs:', error);
      alert('Failed to load available SSCCs. Please try again.', 'error');
    }
  };

  // 01B
  const resetAddSSCCToManifestState = () => {
    setOpenAddSSCCToManifestDialog(false);
    setAddSSCCToManifestData(null);
    setAddSSCCToManifestSelectedId('');
    setAddSSCCToManifestReason('');
    setAddSSCCToManifestWeight('0');
    setAddSSCCToManifestReceiveManifest(true);
    setAvailableSSCCs([]);
  };

  // 01C
  const handleAddSSCCToManifest = async () => {
    const withReceiveManifestCheck = addSSCCToManifestData?.manifest?.status === ManifestStatus.completed;

    // Original logic for assigned SSCCs (adding to manifest)
    // Validation
    if (!addSSCCToManifestData) {
      alert('No manifest selected', 'error');
      return;
    }

    if (!addSSCCToManifestSelectedId) {
      alert('Please select an SSCC to add', 'error');
      return;
    }

    if (!addSSCCToManifestReason.trim()) {
      alert('Reason for adding SSCC is required', 'error');
      return;
    }

    if (isProcessing) {
      return;
    }

    const inventoryTransfer = withReceiveManifestCheck && addSSCCToManifestReceiveManifest;

    try {
      setIsProcessing(true);

      const result = await addSSCCToManifest({
        selectedManifest: addSSCCToManifestData,
        selectedSSCCId: addSSCCToManifestSelectedId,
        ssccWeight: addSSCCToManifestWeight,
        actionReason: addSSCCToManifestReason,
        inventoryTransfer,
        receiveManifestOptionShown: withReceiveManifestCheck,
        alert,
        store,
        user
      });

      if (result) {
        alert(
          `SSCC ${addSSCCToManifestSelectedId} successfully added to manifest ${addSSCCToManifestData.manifest.id}`,
          'success'
        );
        // Clean up state in order
        resetAddSSCCToManifestState();

        // Show Bustle dialog
        setOpenBustleDialog({
          message: 'Access Bustle and Cancel Job',
          messageDetail: 'Please access Bustle to cancel any related jobs for this SSCC addition.'
        });
      } else {
        alert('Failed to add SSCC to manifest. Please verify the data and try again.', 'error');
      }
    } catch (error) {
      console.error('Error adding SSCC to manifest:', error);
      alert('An unexpected error occurred while adding the SSCC. Please try again.', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  // 02A
  const handleOpenMarkCompletedManifestDialog = ({
    tripDisplayStatus,
    transformedManifest,
    manifestStatus,
    tripId,
    legId
  }: {
    tripDisplayStatus: TripLegStatus;
    transformedManifest: Manifest;
    manifestStatus: ManifestStatus;
    tripId: string;
    legId: string;
  }) => {
    const ssccStatus = mapTripLegStatusToSsccStatus(tripDisplayStatus);

    const asnStatus = mapSsccStatusToAsnStatus({
      ssccStatus,
      manifestStatus
    });

    setCompleteManifestData({
      manifest: transformedManifest,
      tripDisplayStatus,
      ssccStatus,
      asnStatus,
      tripId,
      legId
    });
    setOpenCompleteManifestDialog(true);
  };

  // 02B
  const resetCompleteManifestState = () => {
    setOpenCompleteManifestDialog(false);
    setCompleteManifestData(null);
    setCompleteManifestReason('');
  };

  // 02C
  const handleMarkAsCompletedManifest = async () => {
    // Validation
    if (!completeManifestData) {
      alert('No manifest selected to mark as completed', 'error');
      return;
    }

    if (!completeManifestReason.trim()) {
      alert('Reason for completion is required', 'error');
      return;
    }

    if (isProcessing) {
      return;
    }

    try {
      setIsProcessing(true);

      const result = await markManifestCompleted({
        selectedManifest: completeManifestData,
        actionReason: completeManifestReason,
        alert,
        store,
        user,
        onlySendToCoupa: false
      });

      if (result) {
        // Clean up state in order
        resetCompleteManifestState();
      } else {
        alert('Failed to mark manifest as completed. Please verify the data and try again.', 'error');
      }
    } catch (error) {
      console.error('Error marking manifest as completed:', error);
      alert('An unexpected error occurred while completing the manifest. Please try again.', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  // 03A
  const handleOpenDeleteManifestDialog = ({
    tripDisplayStatus,
    manifestStatus,
    transformedManifest,
    tripId,
    legId
  }: {
    tripDisplayStatus: TripLegStatus;
    manifestStatus: ManifestStatus;
    transformedManifest: Manifest;
    tripId: string;
    legId: string;
  }) => {
    const ssccStatus = mapTripLegStatusToSsccStatus(tripDisplayStatus);

    const asnStatus = mapSsccStatusToAsnStatus({
      ssccStatus,
      manifestStatus
    });

    setDeleteManifestData({
      manifest: transformedManifest,
      tripDisplayStatus,
      ssccStatus,
      asnStatus,
      tripId,
      legId
    });
    setOpenDeleteManifestDialog(true);
  };

  // 03B
  const resetDeleteManifestState = () => {
    setOpenDeleteManifestDialog(false);
    setDeleteManifestData(null);
    setDeleteManifestReason('');
  };

  // 03C
  const handleDeleteManifest = async () => {
    // Validation
    if (!deleteManifestData) {
      alert('No manifest selected for deletion', 'error');
      return;
    }

    if (!deleteManifestReason.trim()) {
      alert('Reason for deletion is required', 'error');
      return;
    }

    if (isProcessing) {
      return;
    }

    try {
      setIsProcessing(true);

      const result = await deleteManifest({
        selectedManifest: deleteManifestData,
        actionReason: deleteManifestReason,
        alert,
        store,
        user
      });

      if (result) {
        alert(`Manifest ${deleteManifestData.manifest.id} has been removed.`, 'success');
        // Clean up state in order
        resetDeleteManifestState();

        deleteManifestData.tripDisplayStatus === TripLegStatus.completed && setOpenCoupaDialog(true);
      } else {
        alert('Failed to delete manifest. Please verify the data and try again.', 'error');
      }
    } catch (error) {
      console.error('Error deleting manifest:', error);
      alert('An unexpected error occurred while deleting the manifest. Please try again.', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  // 04A
  const handleOpenAddASNToSSCCDialog = async () => {
    let resolvedParam: SelectedManifestSSCC | SelectedUnassignedSSCC | string | null = null;

    if (ssccActionsContext) {
      const ssccStatus = mapTripLegStatusToSsccStatus(ssccActionsContext.tripDisplayStatus);
      const asnStatus = mapSsccStatusToAsnStatus({
        ssccStatus,
        manifestStatus: ssccActionsContext.manifest.status
      });

      const ssccId = ssccActionsContext.ssccEntry?.ssccId;
      const sscc = ssccActionsContext.ssccEntry;

      if (!ssccId || !sscc) {
        alert('SSCC information is missing, cannot proceed with marking as completed.', 'error');
        return;
      }

      resolvedParam = {
        manifest: ssccActionsContext.transformedManifest,
        tripDisplayStatus: ssccActionsContext.tripDisplayStatus,
        ssccStatus,
        asnStatus,
        tripId: ssccActionsContext.trip.id,
        legId: ssccActionsContext.leg.id,
        ssccId,
        sscc
      };
    } else {
      resolvedParam = ssccActionsPayload?.ssccId || '';
    }

    if (typeof resolvedParam === 'object') {
      const { manifest, tripDisplayStatus, ssccStatus, asnStatus, tripId, legId, ssccId, sscc } = resolvedParam;

      setAddASNToSSCCData({ manifest, tripDisplayStatus, ssccStatus, asnStatus, tripId, legId, ssccId, sscc });
    } else if (typeof resolvedParam === 'string') {
      const sscc = ssccs.find((s) => s.id === resolvedParam);

      setAddASNToUnassignedSSCCData({
        ssccId: resolvedParam,
        sscc,
        ssccStatus: sscc?.status?.toString() || SsccStatus.staged
      });
    }

    setOpenAddASNToSSCCDialog(true);

    // Load available SSCCs that are not already in this manifest
    try {
      const allSSCCsOpen = await store.ssccs.getList('', undefined, 'open', true);
      const allSSCCsStaged = await store.ssccs.getList('', undefined, 'staged', true);
      const allSSCCs = [...(allSSCCsOpen || []), ...(allSSCCsStaged || [])].sort((a, b) =>
        String(b.id).localeCompare(String(a.id), undefined, { numeric: true, sensitivity: 'base' })
      );

      if (allSSCCs) {
        // Filter out SSCCs already in the manifest (if manifest context exists)
        const filteredSSCCs = addASNToSSCCData?.manifest
          ? allSSCCs.filter(
              (sscc) => !addASNToSSCCData.manifest.ssccsWithAsns.map(({ ssccId }) => ssccId).includes(sscc.id)
            )
          : allSSCCs;

        // Fetch destination locations for each SSCC
        const ssccsWithLocations: SSCCProps[] = await Promise.all(
          filteredSSCCs.map(async (s) => {
            const destinationLocation = s.destinationLocationId
              ? await store.locations.getById(s.destinationLocationId)
              : undefined;
            return {
              ...s,
              destinationLocation
            };
          })
        );

        setAvailableSSCCs(ssccsWithLocations);
      }
    } catch (error) {
      console.error('Error loading SSCCs:', error);
      alert('Failed to load available SSCCs. Please try again.', 'error');
    }
  };

  // 04B
  const resetAddASNToSSCCState = () => {
    setOpenAddASNToSSCCDialog(false);
    setAddASNToSSCCData(null);
    setAddASNToUnassignedSSCCData(null);
    setAddASNToSSCCSelectedId('');
    setAddASNToSSCCReceiptEvent('');
    setAddASNToSSCCReason('');
    setAddASNToSSCCReceiveManifest(true);
  };

  // 04C-A
  const handleAddASNToSSCC = async ({
    selectedManifestSSCC,
    tripSheets,
    withReceiveManifestCheck,
    asn,
    ssccId,
    actionReason,
    tripId,
    legId,
    manifestId,
    asnStatus,
    receiptedEventIndex
  }: {
    selectedManifestSSCC: SelectedManifestSSCC | null;
    tripSheets: TripSheetProps[];
    asn: ReceiptEventPackageProps | null;
    withReceiveManifestCheck: boolean;
    ssccId: string;
    actionReason: string;
    tripId?: string;
    legId?: string;
    manifestId?: string;
    asnStatus: string;
    receiptedEventIndex?: number;
  }) => {
    // Validation
    if (!asn) {
      alert('No ASN selected', 'error');
      return;
    }

    if (isProcessing) {
      return;
    }

    const inventoryTransfer = withReceiveManifestCheck && addASNToSSCCReceiveManifest;

    try {
      setIsProcessing(true);

      const result = await addASNToSSCC({
        selectedManifestSSCC,
        tripSheets,
        asn,
        ssccId,
        tripId,
        manifestId,
        legId,
        actionReason,
        receiptedEventIndex,
        asnStatus,
        alert,
        store,
        user,
        inventoryTransfer,
        receiveManifestOptionShown: withReceiveManifestCheck
      });

      if (result) {
        alert(`ASN ${addASNToSSCCSelectedId} successfully added to SSCC ${ssccId}`, 'success');

        // Remove ASN from previous place

        // Clean up state in order
        resetAddASNToSSCCState();
      } else {
        alert('Failed to add ASN to SSCC. Please verify the data and try again.', 'error');
      }
    } catch (error) {
      console.error('Error adding ASN to SSCC:', error);
      alert('An unexpected error occurred while adding the ASN to SSCC. Please try again.', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  // 04C-B
  const handleAddASNToUnassignedSSCC = async ({
    asn,
    ssccId,
    actionReason,
    asnStatus,
    receiptedEventIndex,
    doNotCancelLoading
  }: {
    asn: ReceiptEventPackageProps | null;
    ssccId: string;
    actionReason: string;
    asnStatus: string;
    receiptedEventIndex?: number;
    doNotCancelLoading?: boolean;
  }): Promise<boolean> => {
    // Validation
    if (!asn) {
      alert('No ASN selected', 'error');
      return false;
    }

    if (isProcessing) {
      return false;
    }

    try {
      setIsProcessing(true);

      const result = await addASNToUnassignedSSCC({
        asn,
        ssccId,
        actionReason,
        receiptedEventIndex,
        asnStatus,
        alert,
        store,
        user
      });

      if (result) {
        alert(`ASN ${addASNToSSCCSelectedId} successfully added to SSCC ${ssccId}`, 'success');
        // Clean up state in order

        resetAddASNToSSCCState();
        return true;
      } else {
        alert('Failed to add ASN to SSCC. Please verify the data and try again.', 'error');
        return false;
      }
    } catch (error) {
      console.error('Error adding ASN to SSCC:', error);
      alert('An unexpected error occurred while adding the ASN to SSCC. Please try again.', 'error');
      return false;
    } finally {
      !doNotCancelLoading && setIsProcessing(false);
    }
  };

  // 05A
  const handleOpenMarkCompletedSSCCDialog = () => {
    let resolvedData: SelectedManifestSSCC | SelectedUnassignedSSCC | string | null = null;

    if (ssccActionsContext) {
      const ssccStatus = mapTripLegStatusToSsccStatus(ssccActionsContext.tripDisplayStatus);
      const asnStatus = mapSsccStatusToAsnStatus({
        ssccStatus,
        manifestStatus: ssccActionsContext.manifest.status
      });

      const ssccId = ssccActionsContext.ssccEntry?.ssccId;
      const sscc = ssccActionsContext.ssccEntry;

      if (!ssccId || !sscc) {
        alert('SSCC information is missing, cannot proceed with marking as completed.', 'error');
        return;
      }

      resolvedData = {
        manifest: ssccActionsContext.transformedManifest,
        tripDisplayStatus: ssccActionsContext.tripDisplayStatus,
        ssccStatus,
        asnStatus,
        tripId: ssccActionsContext.trip.id,
        legId: ssccActionsContext.leg.id,
        ssccId,
        sscc
      };
    } else {
      resolvedData = ssccActionsPayload?.ssccId || '';
    }

    if (typeof resolvedData === 'string') {
      const sscc = ssccs.find((s) => s.id === resolvedData);

      setCompleteSSCCData(null);
      setCompleteUnassignedSSCCData({
        ssccId: resolvedData,
        sscc,
        ssccStatus: sscc?.status || SsccStatus.staged
      });
      setCompleteSSCCWeight(sscc?.weight ?? '0');
      setOpenCompleteSSCCDialog(true);
      return;
    }

    if (typeof resolvedData === 'object') {
      if ('manifest' in resolvedData) {
        setCompleteUnassignedSSCCData(null);
        setCompleteSSCCData(resolvedData as SelectedManifestSSCC);
        setCompleteSSCCWeight((resolvedData as SelectedManifestSSCC).sscc?.sscc?.weight ?? '0');
      } else {
        setCompleteSSCCData(null);
        setCompleteUnassignedSSCCData(resolvedData as SelectedUnassignedSSCC);
        setCompleteSSCCWeight((resolvedData as SelectedUnassignedSSCC).sscc?.weight ?? '0');
      }
      setOpenCompleteSSCCDialog(true);
      return;
    }

    alert('Invalid data for marking SSCC as completed.', 'error');
  };

  // 05B
  const resetCompleteSSCCState = () => {
    setOpenCompleteSSCCDialog(false);
    setCompleteSSCCData(null);
    setCompleteUnassignedSSCCData(null);
    setCompleteSSCCReason('');
    setCompleteSSCCWeight('0');
  };

  // 05C-A
  const handleMarkAsCompletedSSCC = async () => {
    // Validation
    if (!completeSSCCData) {
      alert('No SSCC selected to mark as completed', 'error');
      return;
    }

    if (!completeSSCCReason.trim()) {
      alert('Reason for completion is required', 'error');
      return;
    }

    if (isProcessing) {
      return;
    }

    try {
      setIsProcessing(true);

      const result = await markSSCCCompleted({
        selectedManifestSSCC: completeSSCCData,
        actionReason: completeSSCCReason,
        alert,
        store,
        user,
        tripData: tripSheets.find((t) => t.id === completeSSCCData?.tripId),
        ssccWeight: completeSSCCWeight,
        onlySendToCoupa: false
      });

      if (result) {
        // Clean up state in order
        resetCompleteSSCCState();
      } else {
        alert('Failed to mark SSCC as completed. Please verify the data and try again.', 'error');
      }
    } catch (error) {
      console.error('Error marking SSCC as completed:', error);
      alert('An unexpected error occurred while completing the SSCC. Please try again.', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  // 05C-B
  const handleMarkAsCompletedUnassignedSSCC = async () => {
    // Validation
    if (!completeUnassignedSSCCData) {
      alert('No SSCC selected to mark as completed', 'error');
      return;
    }

    if (!completeSSCCReason.trim()) {
      alert('Reason for completion is required', 'error');
      return;
    }

    if (isProcessing) {
      return;
    }

    try {
      setIsProcessing(true);

      const result = await markUnassignedSSCCCompleted({
        sscc: completeUnassignedSSCCData.sscc,
        actionReason: completeSSCCReason,
        alert,
        store,
        user,
        ssccWeight: completeSSCCWeight
      });

      if (result) {
        // Clean up state in order
        resetCompleteSSCCState();
      } else {
        alert('Failed to mark SSCC as completed. Please verify the data and try again.', 'error');
      }
    } catch (error) {
      console.error('Error marking SSCC as completed:', error);
      alert('An unexpected error occurred while completing the SSCC. Please try again.', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  // 06A
  const handleOpenDeleteSSCCDialog = () => {
    if (!ssccActionsContext) {
      alert('This action requires the SSCC to be assigned to a manifest', 'info');
      return;
    }
    const ssccStatus = mapTripLegStatusToSsccStatus(ssccActionsContext.tripDisplayStatus);
    const asnStatus = mapSsccStatusToAsnStatus({
      ssccStatus,
      manifestStatus: ssccActionsContext.manifest.status
    });

    const ssccId = ssccActionsContext.ssccEntry?.ssccId;
    const sscc = ssccActionsContext.ssccEntry;

    if (!ssccId || !sscc) {
      alert('SSCC information is missing, cannot proceed with marking as completed.', 'error');
      return;
    }

    setDeleteSSCCData({
      manifest: ssccActionsContext.transformedManifest,
      tripDisplayStatus: ssccActionsContext.tripDisplayStatus,
      ssccStatus,
      asnStatus,
      tripId: ssccActionsContext.trip.id,
      legId: ssccActionsContext.leg.id,
      ssccId,
      sscc
    });

    setOpenDeleteSSCCDialog(true);
  };

  // 06B
  const resetDeleteSSCCState = () => {
    setOpenDeleteSSCCDialog(false);
    setDeleteSSCCData(null);
    setDeleteSSCCReason('');
  };

  // 06C
  const handleDeleteSSCC = async () => {
    // Validation
    if (!deleteSSCCData) {
      alert('No SSCC selected for deletion', 'error');
      return;
    }

    if (!deleteSSCCReason.trim()) {
      alert('Reason for deletion is required', 'error');
      return;
    }

    if (!deleteSSCCData.ssccId) {
      alert('SSCC identifier is missing. Please close and try again.', 'error');
      return;
    }

    if (isProcessing) {
      return;
    }

    try {
      setIsProcessing(true);

      const result = await deleteSSCC({
        tripId: deleteSSCCData.tripId,
        legId: deleteSSCCData.legId,
        ssccId: deleteSSCCData.ssccId,
        actionReason: deleteSSCCReason,
        alert,
        store,
        user
      });

      if (result) {
        alert('SSCC has been removed.', 'success');
        // Clean up state in order
        resetDeleteSSCCState();

        handleCloseSsccActions();

        deleteSSCCData.tripDisplayStatus === TripLegStatus.completed && setOpenCoupaDialog(true);

        setOpenBustleDialog({
          message: 'Access Bustle, remove job from manifest and reinstate',
          messageDetail:
            'Please access Bustle to remove any related jobs for this SSCC from the manifest and reinstate the SSCC.'
        });
      } else {
        alert('Failed to delete SSCC. Please verify the data and try again.', 'error');
      }
    } catch (error) {
      console.error('Error deleting SSCC:', error);
      alert('An unexpected error occurred while deleting the SSCC. Please try again.', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  // 07A
  const handleOpenDeleteASNDialog = (asn: ReceiptEventPackageProps) => {
    const asnKey = buildAsnKey(asn);
    const asnDuplicateInfo = asnKey ? (asnOccurrences.get(asnKey) ?? []) : [];
    const isDuplicate = asnDuplicateInfo.length > 1;
    const priorityValue = asn.priority || '';
    const normalizedPriority = priorityValue.toUpperCase();
    const priorityShipping = normalizedPriority.includes('P1') || normalizedPriority.includes('P2');

    setDeleteASNData({
      asn,
      asnUniqueId: asn.id || '',
      asnStatus: asn.status || 'Unknown Status',
      priority: asn.priority,
      isDuplicate,
      priorityShipping,
      tripDisplayStatus: ssccActionsContext?.tripDisplayStatus,
      tripId: ssccActionsContext?.trip.id || null,
      legId: ssccActionsContext?.leg.id || null,
      ssccId: ssccActionsContext?.ssccEntry?.ssccId || ssccActionsPayload?.ssccId || ''
    });
    setDeleteASNTransferSSCCId('');
    setDeleteASNTransferReceiptedEvent('');
    setDeleteASNReason('');
    setOpenDeleteASNDialog(true);
  };

  // 07B
  const resetDeleteASNState = () => {
    setOpenDeleteASNDialog(false);
    setDeleteASNData(null);
    setDeleteASNReason('');
    setDeleteASNTransferSSCCId('');
    setDeleteASNTransferReceiptedEvent('');
  };

  // 07C
  const handleDeleteASN = async () => {
    // Validation
    if (!deleteASNData) {
      alert('No ASN selected for deletion', 'error');
      return;
    }

    if (!deleteASNReason.trim()) {
      alert('Reason for deletion is required', 'error');
      return;
    }

    if (!deleteASNData.asnUniqueId || !deleteASNData.ssccId) {
      alert('Missing required ASN data. Please close and try again.', 'error');
      return;
    }

    if (isProcessing) {
      return;
    }

    try {
      setIsProcessing(true);

      if (!deleteASNData.isDuplicate) {
        const selectedTransferSSCC = ssccs.find((sscc) => sscc.id === deleteASNTransferSSCCId);
        const transferReceiptedEventIndex = selectedTransferSSCC?.receiptedEvents?.findIndex(
          (event, index) => `${index + 1}. ${event.name} - ${event.site}` === deleteASNTransferReceiptedEvent
        );

        // Save to another sscc
        const transferResult = await handleAddASNToUnassignedSSCC({
          asn: deleteASNData.asn,
          ssccId: deleteASNTransferSSCCId,
          actionReason: deleteASNReason,
          asnStatus: selectedTransferSSCC?.status || SsccStatus.staged,
          receiptedEventIndex:
            transferReceiptedEventIndex !== undefined && transferReceiptedEventIndex >= 0
              ? transferReceiptedEventIndex
              : undefined,
          doNotCancelLoading: true
        });

        if (!transferResult) {
          return;
        }
      }

      const result = await deleteASN({
        asnId: deleteASNData.asn.asn || '',
        asnUniqueId: deleteASNData.asnUniqueId,
        tripId: deleteASNData.tripId,
        legId: deleteASNData.legId,
        ssccId: deleteASNData.ssccId,
        priorityShipping: deleteASNData.priorityShipping,
        actionReason: deleteASNReason,
        alert,
        store,
        user
      });

      if (result) {
        alert(`ASN ${deleteASNData.asn.asn} has been removed.`, 'success');
        // Clean up state in order
        resetDeleteASNState();

        deleteASNData.asnStatus === AsnStatus.available && setOpenCoupaDialog(true);
      } else {
        alert('Failed to delete ASN. Please verify the data and try again.', 'error');
      }
    } catch (error) {
      console.error('Error deleting ASN:', error);
      alert('An unexpected error occurred while deleting the ASN. Please try again.', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  if (showHistory) {
    return <ReportingLogs onClose={() => setShowHistory(false)} />;
  }

  return (
    <Stack
      spacing={0}
      sx={{
        mt: `${HEADER_HEIGHT}px`,
        height: `calc(100vh - ${HEADER_HEIGHT}px)`,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
      {/* Filter Section */}
      <Box
        sx={{
          flexShrink: 0,
          position: 'sticky',
          top: 0,
          zIndex: 8,
          px: 3,
          pt: 3,
          pb: 1.5
        }}>
        <Card
          sx={{
            backgroundColor: 'background.paper',
            backgroundImage: 'linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(250,250,252,0.95) 100%)',
            backdropFilter: 'blur(8px)',
            boxShadow: '0 12px 28px rgba(15, 23, 42, 0.12)',
            border: '1px solid rgba(15, 23, 42, 0.1)',
            borderRadius: 3,
            overflow: 'hidden'
          }}>
          <Box
            sx={{
              height: '3px',
              background: 'linear-gradient(90deg, #1976d2 0%, #1565c0 50%, #0d47a1 100%)',
              borderRadius: '3px 3px 0 0'
            }}
          />
          <CardContent sx={{ p: 3, pb: 2.5 }}>
            <Stack spacing={3}>
              {/* Filter Header Section */}
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack
                  width={'100%'}
                  direction={'row'}
                  justifyContent={'space-between'}
                  alignItems={'center'}
                  spacing={2}>
                  {/* Left: Icon, Title, Clear Button */}
                  <Stack direction="row" spacing={1.5} alignItems="center" flex={0}>
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: 1.5,
                        backgroundColor: 'rgba(25, 118, 210, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s ease'
                      }}>
                      <SearchIcon sx={{ color: '#1976d2', fontSize: 20 }} />
                    </Box>
                    <Typography variant="h6" fontWeight={700} sx={{ color: 'text.primary', letterSpacing: '-0.3px' }}>
                      Filters
                    </Typography>

                    {(tripSearch ||
                      legDestinationSearch ||
                      manifestSearch ||
                      manifestTrailerIdSearch ||
                      asnSearch ||
                      asnPoNumberSearch ||
                      ssccSearch ||
                      ssccNicknameSearch ||
                      selectedLegDepartureDate ||
                      selectedLegArrivalDate) && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setTripSearch('');
                          setLegDestinationSearch('');
                          setManifestSearch('');
                          setManifestTrailerIdSearch('');
                          setAsnSearch('');
                          setAsnPoNumberSearch('');
                          setSsccSearch('');
                          setSsccNicknameSearch('');
                          setSelectedLegDepartureDate('');
                          setSelectedLegArrivalDate('');
                          setCurrentPage(1);
                        }}
                        sx={{
                          whiteSpace: 'nowrap',
                          fontSize: '0.80rem',
                          textTransform: 'none',
                          fontWeight: 600,
                          ml: 1,
                          borderColor: 'rgba(25, 118, 210, 0.3)',
                          color: '#1976d2',
                          '&:hover': {
                            borderColor: '#1976d2',
                            backgroundColor: 'rgba(25, 118, 210, 0.06)'
                          }
                        }}>
                        Clear All
                      </Button>
                    )}
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => setShowFilters((prev) => !prev)}
                      endIcon={
                        <ExpandMoreIcon
                          sx={{
                            fontSize: 18,
                            transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s ease'
                          }}
                        />
                      }
                      sx={{
                        whiteSpace: 'nowrap',
                        fontSize: '0.8rem',
                        textTransform: 'none',
                        fontWeight: 600,
                        color: '#1976d2',
                        '&:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.06)'
                        }
                      }}>
                      {showFilters ? 'Hide Filters' : 'Show Filters'}
                    </Button>
                  </Stack>

                  {/* Right: Toggle Switches */}
                  <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="flex-end" flex={1}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={hideEmptyManifestTrips}
                          onChange={handleHideEmptyManifestTripsToggle}
                          size="small"
                        />
                      }
                      label={
                        <Typography
                          variant="caption"
                          fontWeight={500}
                          sx={{ color: 'text.secondary', fontSize: '0.80rem' }}>
                          Hide Empty
                        </Typography>
                      }
                    />
                    <Box sx={{ width: '1px', height: '20px', backgroundColor: 'rgba(15, 23, 42, 0.1)' }} />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={showOnlyManifestDuplicates}
                          onChange={handleShowOnlyManifestDuplicatesToggle}
                          size="small"
                        />
                      }
                      label={
                        <Typography
                          variant="caption"
                          fontWeight={500}
                          sx={{ color: 'text.secondary', fontSize: '0.80rem' }}>
                          Manifest Dupes
                        </Typography>
                      }
                    />
                    <Box sx={{ width: '1px', height: '20px', backgroundColor: 'rgba(15, 23, 42, 0.1)' }} />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={showOnlySSCCDuplicates}
                          onChange={handleShowOnlySSCCDuplicatesToggle}
                          size="small"
                        />
                      }
                      label={
                        <Typography
                          variant="caption"
                          fontWeight={500}
                          sx={{ color: 'text.secondary', fontSize: '0.80rem' }}>
                          SSCC Dupes
                        </Typography>
                      }
                    />
                    <Box sx={{ width: '1px', height: '20px', backgroundColor: 'rgba(15, 23, 42, 0.1)' }} />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={showOnlyASNDuplicates}
                          onChange={handleShowOnlyASNDuplicatesToggle}
                          size="small"
                        />
                      }
                      label={
                        <Typography
                          variant="caption"
                          fontWeight={500}
                          sx={{ color: 'text.secondary', fontSize: '0.80rem' }}>
                          ASN Dupes
                        </Typography>
                      }
                    />
                    <Box sx={{ width: '1px', height: '20px', backgroundColor: 'rgba(15, 23, 42, 0.1)' }} />
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setShowHistory(true);
                      }}
                      sx={{
                        whiteSpace: 'nowrap',
                        fontSize: '0.80rem',
                        textTransform: 'none',
                        fontWeight: 600,
                        borderColor: 'rgba(25, 118, 210, 0.3)',
                        color: '#1976d2',
                        '&:hover': {
                          borderColor: '#1976d2',
                          backgroundColor: 'rgba(25, 118, 210, 0.06)'
                        }
                      }}>
                      History
                    </Button>
                  </Stack>
                </Stack>
              </Stack>

              {/* Search Fields Section */}
              {showFilters && (
                <>
                  {/* Divider */}
                  <Box sx={{ height: '1px', backgroundColor: 'rgba(15, 23, 42, 0.08)', borderRadius: '1px' }} />

                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={2} gap={2} alignItems="flex-start" width="100%">
                      {/* Trip Column */}
                      <Stack direction="column" spacing={1.2} alignItems="stretch" flex={1}>
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          sx={{
                            color: 'text.secondary',
                            fontSize: '0.75rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            ml: 0.5
                          }}>
                          Trip
                        </Typography>
                        <Stack direction="column" spacing={1} alignItems="stretch">
                          <TextField
                            fullWidth
                            placeholder="Trip Number..."
                            value={tripSearch}
                            onChange={(e) => handleTripSearchChange(e.target.value)}
                            size="small"
                            InputProps={{
                              startAdornment: (
                                <LocalShippingIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 19 }} />
                              ),
                              endAdornment: tripSearch ? (
                                <IconButton size="small" onClick={() => handleTripSearchChange('')} sx={{ p: 0.5 }}>
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              ) : null
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: tripSearch ? 'rgba(25, 118, 210, 0.08)' : 'rgba(250, 250, 252, 0.6)',
                                fontSize: '0.85rem',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: tripSearch ? 'rgba(25, 118, 210, 0.12)' : 'rgba(250, 250, 252, 0.9)'
                                }
                              }
                            }}
                          />
                          <TextField
                            fullWidth
                            placeholder="Destination..."
                            value={legDestinationSearch}
                            onChange={(e) => handleLegDestinationSearchChange(e.target.value)}
                            size="small"
                            InputProps={{
                              startAdornment: <LocationOnIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 19 }} />,
                              endAdornment: legDestinationSearch ? (
                                <IconButton
                                  size="small"
                                  onClick={() => handleLegDestinationSearchChange('')}
                                  sx={{ p: 0.5 }}>
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              ) : null
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: legDestinationSearch
                                  ? 'rgba(25, 118, 210, 0.08)'
                                  : 'rgba(250, 250, 252, 0.6)',
                                fontSize: '0.85rem',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: legDestinationSearch
                                    ? 'rgba(25, 118, 210, 0.12)'
                                    : 'rgba(250, 250, 252, 0.9)'
                                }
                              }
                            }}
                          />
                        </Stack>
                      </Stack>

                      {/* Manifest Column */}
                      <Stack direction="column" spacing={1.2} alignItems="stretch" flex={1}>
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          sx={{
                            color: 'text.secondary',
                            fontSize: '0.75rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            ml: 0.5
                          }}>
                          Manifest
                        </Typography>
                        <Stack direction="column" spacing={1} alignItems="stretch">
                          <TextField
                            fullWidth
                            placeholder="Manifest Number..."
                            value={manifestSearch}
                            onChange={(e) => handleManifestSearchChange(e.target.value)}
                            size="small"
                            InputProps={{
                              startAdornment: <InventoryIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 19 }} />,
                              endAdornment: manifestSearch ? (
                                <IconButton size="small" onClick={() => handleManifestSearchChange('')} sx={{ p: 0.5 }}>
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              ) : null
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: manifestSearch
                                  ? 'rgba(25, 118, 210, 0.08)'
                                  : 'rgba(250, 250, 252, 0.6)',
                                fontSize: '0.85rem',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: manifestSearch
                                    ? 'rgba(25, 118, 210, 0.12)'
                                    : 'rgba(250, 250, 252, 0.9)'
                                }
                              }
                            }}
                          />
                          <TextField
                            fullWidth
                            placeholder="Trailer ID..."
                            value={manifestTrailerIdSearch}
                            onChange={(e) => handleManifestTrailerIdSearchChange(e.target.value)}
                            size="small"
                            InputProps={{
                              startAdornment: <StorageIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 19 }} />,
                              endAdornment: manifestTrailerIdSearch ? (
                                <IconButton
                                  size="small"
                                  onClick={() => handleManifestTrailerIdSearchChange('')}
                                  sx={{ p: 0.5 }}>
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              ) : null
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: manifestTrailerIdSearch
                                  ? 'rgba(25, 118, 210, 0.08)'
                                  : 'rgba(250, 250, 252, 0.6)',
                                fontSize: '0.85rem',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: manifestTrailerIdSearch
                                    ? 'rgba(25, 118, 210, 0.12)'
                                    : 'rgba(250, 250, 252, 0.9)'
                                }
                              }
                            }}
                          />
                        </Stack>
                      </Stack>

                      {/* SSCC Column */}
                      <Stack direction="column" spacing={1.2} alignItems="stretch" flex={1}>
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          sx={{
                            color: 'text.secondary',
                            fontSize: '0.75rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            ml: 0.5
                          }}>
                          SSCC
                        </Typography>
                        <Stack direction="column" spacing={1} alignItems="stretch">
                          <TextField
                            fullWidth
                            placeholder="SSCC..."
                            value={ssccSearch}
                            onChange={(e) => handleSsccSearchChange(e.target.value)}
                            size="small"
                            InputProps={{
                              startAdornment: <AssignmentIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 19 }} />,
                              endAdornment: ssccSearch ? (
                                <IconButton size="small" onClick={() => handleSsccSearchChange('')} sx={{ p: 0.5 }}>
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              ) : null
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: ssccSearch ? 'rgba(25, 118, 210, 0.08)' : 'rgba(250, 250, 252, 0.6)',
                                fontSize: '0.85rem',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: ssccSearch ? 'rgba(25, 118, 210, 0.12)' : 'rgba(250, 250, 252, 0.9)'
                                }
                              }
                            }}
                          />
                          <TextField
                            fullWidth
                            placeholder="Nickname..."
                            value={ssccNicknameSearch}
                            onChange={(e) => handleSsccNicknameSearchChange(e.target.value)}
                            size="small"
                            InputProps={{
                              startAdornment: <LabelIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 19 }} />,
                              endAdornment: ssccNicknameSearch ? (
                                <IconButton
                                  size="small"
                                  onClick={() => handleSsccNicknameSearchChange('')}
                                  sx={{ p: 0.5 }}>
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              ) : null
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: ssccNicknameSearch
                                  ? 'rgba(25, 118, 210, 0.08)'
                                  : 'rgba(250, 250, 252, 0.6)',
                                fontSize: '0.85rem',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: ssccNicknameSearch
                                    ? 'rgba(25, 118, 210, 0.12)'
                                    : 'rgba(250, 250, 252, 0.9)'
                                }
                              }
                            }}
                          />
                        </Stack>
                      </Stack>

                      {/* ASN Column */}
                      <Stack direction="column" spacing={1.2} alignItems="stretch" flex={1}>
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          sx={{
                            color: 'text.secondary',
                            fontSize: '0.75rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            ml: 0.5
                          }}>
                          ASN
                        </Typography>
                        <Stack direction="column" spacing={1} alignItems="stretch">
                          <TextField
                            fullWidth
                            placeholder="ASN..."
                            value={asnSearch}
                            onChange={(e) => handleAsnSearchChange(e.target.value)}
                            size="small"
                            InputProps={{
                              startAdornment: <DescriptionIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 19 }} />,
                              endAdornment: asnSearch ? (
                                <IconButton size="small" onClick={() => handleAsnSearchChange('')} sx={{ p: 0.5 }}>
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              ) : null
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: asnSearch ? 'rgba(25, 118, 210, 0.08)' : 'rgba(250, 250, 252, 0.6)',
                                fontSize: '0.85rem',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: asnSearch ? 'rgba(25, 118, 210, 0.12)' : 'rgba(250, 250, 252, 0.9)'
                                }
                              }
                            }}
                          />

                          <TextField
                            fullWidth
                            placeholder="PO Number..."
                            value={asnPoNumberSearch}
                            onChange={(e) => handleAsnPoNumberSearchChange(e.target.value)}
                            size="small"
                            InputProps={{
                              startAdornment: (
                                <ShoppingCartIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 19 }} />
                              ),
                              endAdornment: asnPoNumberSearch ? (
                                <IconButton
                                  size="small"
                                  onClick={() => handleAsnPoNumberSearchChange('')}
                                  sx={{ p: 0.5 }}>
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              ) : null
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: asnPoNumberSearch
                                  ? 'rgba(25, 118, 210, 0.08)'
                                  : 'rgba(250, 250, 252, 0.6)',
                                fontSize: '0.85rem',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: asnPoNumberSearch
                                    ? 'rgba(25, 118, 210, 0.12)'
                                    : 'rgba(250, 250, 252, 0.9)'
                                }
                              }
                            }}
                          />
                        </Stack>
                      </Stack>

                      {/* Date Column */}
                      <Stack direction="column" spacing={1.2} alignItems="stretch" flex={1}>
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          sx={{
                            color: 'text.secondary',
                            fontSize: '0.75rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            ml: 0.5
                          }}>
                          Dates
                        </Typography>
                        <Stack direction="column" spacing={1} alignItems="stretch">
                          <TextField
                            fullWidth
                            type="date"
                            label="Departure"
                            value={selectedLegDepartureDate}
                            onChange={(e) => handleLegDepartureDateChange(e.target.value)}
                            size="small"
                            InputLabelProps={{
                              shrink: true
                            }}
                            InputProps={{
                              endAdornment: selectedLegDepartureDate ? (
                                <InputAdornment position="end">
                                  <IconButton
                                    size="small"
                                    aria-label="Clear leg departure date"
                                    onClick={() => handleLegDepartureDateChange('')}>
                                    <ClearIcon fontSize="small" />
                                  </IconButton>
                                </InputAdornment>
                              ) : null
                            }}
                            sx={{
                              minWidth: 'auto',
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: selectedLegDepartureDate
                                  ? 'rgba(25, 118, 210, 0.08)'
                                  : 'rgba(250, 250, 252, 0.6)',
                                fontSize: '0.85rem',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: selectedLegDepartureDate
                                    ? 'rgba(25, 118, 210, 0.12)'
                                    : 'rgba(250, 250, 252, 0.9)'
                                }
                              }
                            }}
                          />
                          <TextField
                            fullWidth
                            type="date"
                            label="Arrival"
                            value={selectedLegArrivalDate}
                            onChange={(e) => handleLegArrivalDateChange(e.target.value)}
                            size="small"
                            InputLabelProps={{
                              shrink: true
                            }}
                            InputProps={{
                              endAdornment: selectedLegArrivalDate ? (
                                <InputAdornment position="end">
                                  <IconButton
                                    size="small"
                                    aria-label="Clear leg arrival date"
                                    onClick={() => handleLegArrivalDateChange('')}>
                                    <ClearIcon fontSize="small" />
                                  </IconButton>
                                </InputAdornment>
                              ) : null
                            }}
                            sx={{
                              minWidth: 'auto',
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: selectedLegArrivalDate
                                  ? 'rgba(25, 118, 210, 0.08)'
                                  : 'rgba(250, 250, 252, 0.6)',
                                fontSize: '0.85rem',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: selectedLegArrivalDate
                                    ? 'rgba(25, 118, 210, 0.12)'
                                    : 'rgba(250, 250, 252, 0.9)'
                                }
                              }
                            }}
                          />
                        </Stack>
                      </Stack>
                    </Stack>
                  </Stack>
                </>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Box>

      {/* Trip Sheets Header - Sticky */}
      {filteredTripSheets.length > 0 && (
        <Box
          sx={{
            flexShrink: 0,
            position: 'sticky',
            top: 0,
            zIndex: 7,
            px: 3,
            py: 1.5,
            backgroundColor: 'rgba(255,255,255,0.95)',
            borderBottom: '1px solid rgba(0,0,0,0.08)',
            backdropFilter: 'blur(4px)'
          }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box
                  sx={{
                    p: 0.75,
                    borderRadius: 1.2,
                    backgroundColor: 'rgba(25, 118, 210, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                  <LocalShippingIcon sx={{ color: '#1976d2', fontSize: 18 }} />
                </Box>
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  sx={{ color: 'text.primary', letterSpacing: '-0.3px' }}>
                  Trip Sheets
                </Typography>
                <Chip
                  label={filteredTripSheets.length}
                  size="small"
                  sx={{
                    height: 24,
                    fontSize: '0.80rem',
                    fontWeight: 700,
                    backgroundColor: 'rgba(25, 118, 210, 0.12)',
                    color: '#1976d2'
                  }}
                />
              </Box>
              <Tooltip
                title={
                  <Box sx={{ p: 1 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                      Management Tools
                    </Typography>
                    <Typography variant="caption" component="div" sx={{ mb: 0.5 }}>
                      • Mark as Completed: Manually complete manifests when system/process issues prevent normal
                      completion
                    </Typography>
                    <Typography variant="caption" component="div" sx={{ mb: 0.5 }}>
                      • Delete Manifest: Permanently remove duplicate or erroneous manifests
                    </Typography>
                    <Typography
                      variant="caption"
                      component="div"
                      sx={{ mt: 1, fontStyle: 'italic', color: 'rgba(255,255,255,0.7)' }}>
                      All actions are logged in the audit trail and require a reason
                    </Typography>
                  </Box>
                }
                arrow
                placement="right">
                <IconButton size="small" sx={{ color: 'text.secondary' }}>
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
            <Stack direction="row" spacing={2.5} alignItems="center">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                  Manifest Dupes
                </Typography>
                <Chip
                  label={Array.from(manifestOccurrences.values()).filter((arr) => arr.length > 1).length}
                  size="small"
                  sx={{
                    height: 24,
                    fontSize: '0.80rem',
                    fontWeight: 600,
                    backgroundColor: 'rgba(211, 47, 47, 0.1)',
                    color: '#c62828'
                  }}
                />
              </Box>
              <Box sx={{ width: '1px', height: '20px', backgroundColor: 'rgba(15, 23, 42, 0.1)' }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                  SSCC Dupes
                </Typography>
                <Chip
                  label={Array.from(ssccOccurrences.values()).filter((arr) => arr.length > 1).length}
                  size="small"
                  sx={{
                    height: 24,
                    fontSize: '0.80rem',
                    fontWeight: 600,
                    backgroundColor: 'rgba(211, 47, 47, 0.1)',
                    color: '#c62828'
                  }}
                />
              </Box>
              <Box sx={{ width: '1px', height: '20px', backgroundColor: 'rgba(15, 23, 42, 0.1)' }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                  ASN Dupes
                </Typography>
                <Chip
                  label={Array.from(asnOccurrences.values()).filter((arr) => arr.length > 1).length}
                  size="small"
                  sx={{
                    height: 24,
                    fontSize: '0.80rem',
                    fontWeight: 600,
                    backgroundColor: 'rgba(211, 47, 47, 0.1)',
                    color: '#c62828'
                  }}
                />
              </Box>
            </Stack>
          </Stack>
        </Box>
      )}

      <Box
        ref={contentScrollRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          px: 3,
          pt: 1,
          pb: 2,
          '&::-webkit-scrollbar': {
            width: '8px'
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent'
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(15, 23, 42, 0.2)',
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: 'rgba(15, 23, 42, 0.3)'
            }
          }
        }}>
        <Box sx={{ maxWidth: '85%', margin: '0 auto', width: '100%' }}>
          {/* Unassigned SSCCs Section */}
          {currentPage === 1 && (
            <Card
              sx={{
                mb: 3,
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                border: '1px solid rgba(0,0,0,0.08)',
                borderRadius: 3,
                overflow: 'hidden'
              }}>
              <Box
                sx={{
                  height: '3px',
                  background: 'linear-gradient(90deg, #ed6c02 0%, #e65100 100%)'
                }}
              />
              <CardContent sx={{ p: 3 }}>
                <Stack spacing={2}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        backgroundColor: 'rgba(237, 108, 2, 0.08)',
                        display: 'flex'
                      }}>
                      <InventoryIcon sx={{ fontSize: 24, color: '#ed6c02' }} />
                    </Box>
                    <Box>
                      <Typography variant="h6" fontWeight={700} sx={{ color: 'text.primary' }}>
                        Unassigned SSCCs
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                        SSCCs not assigned to any trip, leg, or manifest
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }} />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={showUnassignedSSCCs}
                          onChange={(e) => setShowUnassignedSSCCs(e.target.checked)}
                          size="small"
                        />
                      }
                      label={
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          sx={{ color: 'text.secondary', fontSize: '0.80rem' }}>
                          Unassigned SSCCs
                        </Typography>
                      }
                      sx={{ mr: 0.5 }}
                    />
                    <Chip
                      label={`${unassignedSSCCs.length} ${unassignedSSCCs.length === 1 ? 'SSCC' : 'SSCCs'}`}
                      size="medium"
                      sx={{
                        height: 32,
                        fontSize: '0.875rem',
                        fontWeight: 700,
                        backgroundColor: 'rgba(237, 108, 2, 0.12)',
                        color: '#ed6c02'
                      }}
                    />
                  </Stack>

                  {showUnassignedSSCCs && (
                    <>
                      <Divider sx={{ my: 1 }} />

                      {unassignedSSCCs.length > 0 ? (
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                            gap: 2,
                            maxHeight: '400px',
                            overflowY: 'auto',
                            pr: 1,
                            '&::-webkit-scrollbar': {
                              width: '6px'
                            },
                            '&::-webkit-scrollbar-track': {
                              backgroundColor: 'rgba(0,0,0,0.05)',
                              borderRadius: '3px'
                            },
                            '&::-webkit-scrollbar-thumb': {
                              backgroundColor: 'rgba(0,0,0,0.2)',
                              borderRadius: '3px',
                              '&:hover': {
                                backgroundColor: 'rgba(0,0,0,0.3)'
                              }
                            }
                          }}>
                          {paginatedUnassignedSSCCs.map((sscc) => {
                            const duplicateAsnCount = paginatedUnassignedDuplicateAsnCountById.get(sscc.id) ?? 0;

                            return (
                              <Card
                                key={sscc.id}
                                onClick={() => {
                                  setSsccActionsPayload({
                                    tripId: '',
                                    legId: '',
                                    manifestId: '',
                                    ssccId: sscc.id,
                                    sscc: ssccById.get(sscc.id)
                                  });
                                  setSsccActionsOpen(true);
                                }}
                                sx={{
                                  border: '1px solid rgba(0,0,0,0.08)',
                                  borderRadius: 2,
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                                  transition: 'all 0.2s ease',
                                  cursor: 'pointer',
                                  '&:hover': {
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                                    borderColor: 'rgba(237, 108, 2, 0.3)',
                                    transform: 'translateY(-2px)'
                                  }
                                }}>
                                <CardContent sx={{ p: 2 }}>
                                  <Stack spacing={1.5}>
                                    <Stack direction="row" alignItems="center" spacing={1.5}>
                                      <Box
                                        sx={{
                                          p: 1,
                                          borderRadius: 1.5,
                                          backgroundColor: 'rgba(237, 108, 2, 0.08)',
                                          display: 'flex'
                                        }}>
                                        <InventoryIcon sx={{ fontSize: 20, color: '#ed6c02' }} />
                                      </Box>
                                      <Box sx={{ flex: 1 }}>
                                        <Typography variant="body2" fontWeight={700} sx={{ color: 'text.primary' }}>
                                          {sscc.id}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                          {sscc.nickname || 'No nickname'}
                                        </Typography>
                                      </Box>
                                      {duplicateAsnCount > 0 && (
                                        <Chip
                                          label={`ASN Dupes: ${duplicateAsnCount}`}
                                          size="small"
                                          sx={{
                                            height: 20,
                                            fontSize: '0.70rem',
                                            fontWeight: 600,
                                            backgroundColor: 'rgba(211, 47, 47, 0.1)',
                                            color: '#c62828'
                                          }}
                                        />
                                      )}
                                    </Stack>

                                    <Divider />

                                    <Stack spacing={0.5}>
                                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Typography
                                          variant="caption"
                                          sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                          Status:
                                        </Typography>
                                        <Chip
                                          label={sscc.status || 'Unknown'}
                                          size="small"
                                          sx={{
                                            height: 20,
                                            fontSize: '0.70rem',
                                            fontWeight: 600,
                                            backgroundColor:
                                              sscc.status === 'open'
                                                ? 'rgba(46, 125, 50, 0.1)'
                                                : sscc.status === 'staged'
                                                  ? 'rgba(25, 118, 210, 0.1)'
                                                  : 'rgba(158, 158, 158, 0.1)',
                                            color:
                                              sscc.status === 'open'
                                                ? '#2e7d32'
                                                : sscc.status === 'staged'
                                                  ? '#1976d2'
                                                  : '#616161'
                                          }}
                                        />
                                      </Stack>
                                      {sscc.destinationLocation && (
                                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                          <Typography
                                            variant="caption"
                                            sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                            Destination:
                                          </Typography>
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              color: 'text.primary',
                                              fontSize: '0.75rem',
                                              fontWeight: 600,
                                              textAlign: 'right',
                                              maxWidth: '60%'
                                            }}>
                                            {sscc.destinationLocation.name}
                                          </Typography>
                                        </Stack>
                                      )}
                                      {sscc.weight && (
                                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                                          <Typography
                                            variant="caption"
                                            sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                            Weight:
                                          </Typography>
                                          <Typography
                                            variant="caption"
                                            sx={{ color: 'text.primary', fontSize: '0.75rem', fontWeight: 600 }}>
                                            {sscc.weight} kg
                                          </Typography>
                                        </Stack>
                                      )}
                                    </Stack>
                                  </Stack>
                                </CardContent>
                              </Card>
                            );
                          })}
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            py: 2,
                            px: 2,
                            borderRadius: 1,
                            backgroundColor: 'rgba(0,0,0,0.02)',
                            border: '1px solid rgba(0,0,0,0.08)'
                          }}>
                          <Typography variant="body2" color="text.secondary" fontStyle="italic" fontSize="0.85rem">
                            No unassigned SSCCs match the current filters.
                          </Typography>
                        </Box>
                      )}

                      {unassignedSSCCs.length > UNASSIGNED_SSCC_PAGE_SIZE && (
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                            Showing {paginatedUnassignedSSCCs.length} of {unassignedSSCCs.length} SSCCs
                          </Typography>
                          <Pagination
                            count={unassignedSSCCTotalPages}
                            page={unassignedSSCCPage}
                            onChange={handleUnassignedSSCCPageChange}
                            color="primary"
                            size="small"
                          />
                        </Stack>
                      )}
                    </>
                  )}
                </Stack>
              </CardContent>
            </Card>
          )}

          {tripSheets.length === 0 && !tripSearch ? (
            <Box
              sx={{
                textAlign: 'center',
                py: 10,
                px: 3,
                borderRadius: 2
              }}>
              <Box
                sx={{
                  p: 3,
                  borderRadius: 2,
                  backgroundColor: 'background.default',
                  border: '1px dashed rgba(0,0,0,0.12)',
                  mb: 3,
                  display: 'inline-block'
                }}>
                <CircularProgress sx={{ color: '#1976d2' }} size={48} />
              </Box>
              <Typography variant="h5" color="text.primary" fontWeight={700} sx={{ mt: 2 }}>
                Loading Trip Sheets
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Syncing data from DataStore
              </Typography>
            </Box>
          ) : filteredTripSheets.length === 0 ? (
            <Box
              sx={{
                textAlign: 'center',
                py: 10,
                px: 3,
                borderRadius: 2
              }}>
              <Box
                sx={{
                  p: 3,
                  borderRadius: 2,
                  backgroundColor: 'background.default',
                  border: '1px dashed rgba(0,0,0,0.12)',
                  mb: 3,
                  display: 'inline-block'
                }}>
                <LocalShippingIcon sx={{ fontSize: 56, color: 'rgba(0,0,0,0.2)' }} />
              </Box>
              <Typography variant="h5" color="text.primary" fontWeight={700} sx={{ mt: 2 }}>
                No Trip Sheets Found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Try adjusting your search or filter criteria
              </Typography>
              <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                  Unassigned SSCCs
                </Typography>
                <Chip
                  size="small"
                  label={showUnassignedSSCCs ? 'Visible' : 'Hidden'}
                  sx={{
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    backgroundColor: showUnassignedSSCCs ? 'rgba(46, 125, 50, 0.12)' : 'rgba(0,0,0,0.08)',
                    color: showUnassignedSSCCs ? '#2e7d32' : 'text.secondary'
                  }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                  {unassignedSSCCs.length} matching
                </Typography>
              </Stack>
            </Box>
          ) : (
            <Stack spacing={2}>
              {paginatedTripSheets.map((trip: TripSheetProps) => {
                const manifestDupeCount = new Set(
                  trip.legs
                    .flatMap((leg) => leg.manifests.map((manifest) => manifest.id))
                    .filter((manifestId) => (manifestOccurrences.get(manifestId)?.length ?? 0) > 1)
                ).size;

                const ssccDupeCount = new Set(
                  trip.legs
                    .flatMap((leg) =>
                      leg.manifests.flatMap((manifest) => manifest.ssccs?.map((sscc) => sscc.ssccId) || [])
                    )
                    .filter((ssccId) => (ssccOccurrences.get(ssccId)?.length ?? 0) > 1)
                ).size;

                const asnDupeCount = new Set(
                  trip.legs
                    .flatMap((leg) =>
                      leg.manifests.flatMap(
                        (m) =>
                          m.ssccs?.flatMap(
                            (s) => s.receiptedEvents?.flatMap((r) => r.packages.flatMap((p) => p) || []) || []
                          ) || []
                      )
                    )
                    .filter((pkg) => {
                      if (!pkg) {
                        return false;
                      }
                      const asnKey = buildAsnKey(pkg);
                      return asnKey ? (asnOccurrences.get(asnKey)?.length ?? 0) > 1 : false;
                    })
                ).size;

                return (
                  <Accordion
                    key={trip.id}
                    sx={{
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      border: '1px solid rgba(0,0,0,0.08)',
                      borderRadius: '12px !important',
                      backgroundColor: '#ffffff',
                      '&:before': { display: 'none' },
                      mb: 2,
                      position: 'relative',
                      zIndex: 0,
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
                        borderColor: 'rgba(25, 118, 210, 0.2)'
                      }
                    }}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        padding: '16px 20px',
                        '&:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.02)'
                        }
                      }}>
                      <Stack
                        direction="row"
                        spacing={2.5}
                        alignItems="flex-start"
                        sx={{
                          width: '100%',
                          pr: 2,
                          py: 1
                        }}>
                        {/* Icon */}
                        <Box
                          sx={{
                            p: 1.75,
                            borderRadius: 2,
                            backgroundColor:
                              'linear-gradient(135deg, rgba(25, 118, 210, 0.12) 0%, rgba(25, 118, 210, 0.04) 100%)',
                            border: '1px solid rgba(25, 118, 210, 0.15)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: 56
                          }}>
                          <LocalShippingIcon sx={{ color: '#1976d2', fontSize: 26, fontWeight: 500 }} />
                        </Box>

                        {/* Trip Info */}
                        <Box sx={{ flex: 1 }}>
                          <Stack spacing={1}>
                            <Stack direction="row" spacing={1.5} alignItems="center">
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontWeight: 700,
                                  letterSpacing: '0.3px',
                                  color: 'text.primary',
                                  fontSize: '1rem'
                                }}>
                                Trip #{trip.reference || trip.id}
                              </Typography>
                            </Stack>

                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 500,
                                  color: 'text.primary',
                                  fontSize: '0.9rem'
                                }}>
                                {trip.carrier?.businessName || 'Unknown Carrier'}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'text.secondary',
                                  fontSize: '0.8rem'
                                }}>
                                •
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'text.secondary',
                                  fontSize: '0.85rem'
                                }}>
                                {trip.createdAt ? dayjs(trip.createdAt).format(dateFormat) : 'N/A'}
                              </Typography>
                            </Stack>

                            {/* Duplicate Stats - Horizontal Below Date */}
                            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1 }}>
                              {manifestDupeCount ? (
                                <Box
                                  sx={{
                                    minWidth: '165px',
                                    px: 2,
                                    py: 1.25,
                                    borderRadius: 2,
                                    backgroundColor: 'rgba(211, 47, 47, 0.08)',
                                    border: '1.5px solid rgba(211, 47, 47, 0.2)',
                                    boxShadow: '0 2px 8px rgba(211, 47, 47, 0.1)'
                                  }}>
                                  <Stack direction="row" spacing={1} alignItems="center">
                                    <InventoryIcon sx={{ fontSize: 20, color: '#c62828' }} />
                                    <Stack spacing={0.25}>
                                      <Typography
                                        sx={{
                                          fontWeight: 800,
                                          fontSize: '1.1rem',
                                          lineHeight: 1,
                                          color: '#c62828',
                                          letterSpacing: '-0.5px'
                                        }}>
                                        {manifestDupeCount}
                                      </Typography>
                                      <Typography
                                        sx={{
                                          fontSize: '0.7rem',
                                          fontWeight: 600,
                                          color: '#b71c1c',
                                          lineHeight: 1,
                                          textTransform: 'uppercase',
                                          letterSpacing: '0.3px'
                                        }}>
                                        Manifest Dupes
                                      </Typography>
                                    </Stack>
                                  </Stack>
                                </Box>
                              ) : null}
                              {ssccDupeCount ? (
                                <Box
                                  sx={{
                                    minWidth: '165px',
                                    px: 2,
                                    py: 1.25,
                                    borderRadius: 2,
                                    backgroundColor: 'rgba(211, 47, 47, 0.08)',
                                    border: '1.5px solid rgba(211, 47, 47, 0.2)',
                                    boxShadow: '0 2px 8px rgba(211, 47, 47, 0.1)'
                                  }}>
                                  <Stack direction="row" spacing={1} alignItems="center">
                                    <AssignmentIcon sx={{ fontSize: 20, color: '#c62828' }} />
                                    <Stack spacing={0.25}>
                                      <Typography
                                        sx={{
                                          fontWeight: 800,
                                          fontSize: '1.1rem',
                                          lineHeight: 1,
                                          color: '#c62828',
                                          letterSpacing: '-0.5px'
                                        }}>
                                        {ssccDupeCount}
                                      </Typography>
                                      <Typography
                                        sx={{
                                          fontSize: '0.7rem',
                                          fontWeight: 600,
                                          color: '#b71c1c',
                                          lineHeight: 1,
                                          textTransform: 'uppercase',
                                          letterSpacing: '0.3px'
                                        }}>
                                        SSCC Dupes
                                      </Typography>
                                    </Stack>
                                  </Stack>
                                </Box>
                              ) : null}
                              {asnDupeCount ? (
                                <Box
                                  sx={{
                                    minWidth: '165px',
                                    px: 2,
                                    py: 1.25,
                                    borderRadius: 2,
                                    backgroundColor: 'rgba(211, 47, 47, 0.08)',
                                    border: '1.5px solid rgba(211, 47, 47, 0.2)',
                                    boxShadow: '0 2px 8px rgba(211, 47, 47, 0.1)'
                                  }}>
                                  <Stack direction="row" spacing={1} alignItems="center">
                                    <DescriptionIcon sx={{ fontSize: 20, color: '#c62828' }} />
                                    <Stack spacing={0.25}>
                                      <Typography
                                        sx={{
                                          fontWeight: 800,
                                          fontSize: '1.1rem',
                                          lineHeight: 1,
                                          color: '#c62828',
                                          letterSpacing: '-0.5px'
                                        }}>
                                        {asnDupeCount}
                                      </Typography>
                                      <Typography
                                        sx={{
                                          fontSize: '0.7rem',
                                          fontWeight: 600,
                                          color: '#b71c1c',
                                          lineHeight: 1,
                                          textTransform: 'uppercase',
                                          letterSpacing: '0.3px'
                                        }}>
                                        ASN Dupes
                                      </Typography>
                                    </Stack>
                                  </Stack>
                                </Box>
                              ) : null}
                            </Stack>
                          </Stack>
                        </Box>

                        <Box
                          sx={{
                            minWidth: 480,
                            maxWidth: 560,
                            pl: 3,
                            pr: 1,
                            borderLeft: '2px solid rgba(25, 118, 210, 0.1)',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center'
                          }}>
                          <Stack spacing={1.25}>
                            {trip.legs.map((leg, legIndex) => (
                              <Box
                                key={leg.id + legIndex}
                                sx={{
                                  width: '100%'
                                }}>
                                <Box
                                  sx={{
                                    px: 2.5,
                                    py: 1.25,
                                    my: 0.75,
                                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                    borderRadius: 2,
                                    border: '1px solid rgba(25, 118, 210, 0.12)',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                      backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                      borderColor: 'rgba(25, 118, 210, 0.2)'
                                    }
                                  }}>
                                  <Stack direction="row" spacing={1.5} alignItems="center">
                                    <Typography
                                      sx={{
                                        fontSize: '0.75rem',
                                        fontWeight: 700,
                                        color: '#1976d2',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        minWidth: 'fit-content'
                                      }}>
                                      Leg {legIndex + 1}
                                    </Typography>
                                    <Typography sx={{ color: 'rgba(0,0,0,0.3)', fontWeight: 500 }}>|</Typography>
                                    <Typography
                                      sx={{
                                        fontSize: '0.85rem',
                                        fontWeight: 600,
                                        color: 'text.primary'
                                      }}>
                                      {leg.origin?.businessName || 'Unknown'}
                                    </Typography>
                                    <Typography sx={{ color: 'rgba(25, 118, 210, 0.6)', fontSize: '0.9rem' }}>
                                      →
                                    </Typography>
                                    <Typography
                                      sx={{
                                        fontSize: '0.85rem',
                                        fontWeight: 600,
                                        color: 'text.primary'
                                      }}>
                                      {leg.destination?.businessName || 'Unknown'}
                                    </Typography>
                                  </Stack>
                                </Box>

                                <Stack
                                  direction="row"
                                  spacing={0.75}
                                  flexWrap="wrap"
                                  sx={{ gap: 0.75, pl: 0.5, mt: 1 }}>
                                  {leg.manifests.length === 0 ? (
                                    <Typography
                                      variant="body2"
                                      sx={{ fontSize: '0.8rem', pl: 1, fontStyle: 'italic' }}
                                      color="text.secondary">
                                      No manifests
                                    </Typography>
                                  ) : (
                                    leg.manifests.map((manifest, manifestIndex) => {
                                      const isDuplicate = (manifestOccurrences.get(manifest.id)?.length ?? 0) > 1;
                                      const tripDisplayStatus = getTripDisplayStatus({
                                        manifestStatus: manifest.status,
                                        legStatus: leg.status
                                      });

                                      return (
                                        <Box
                                          key={`${legIndex}-${manifest.id}-${manifestIndex}`}
                                          sx={{
                                            px: 2,
                                            py: 1.5,
                                            minWidth: 140,
                                            borderRadius: 2,
                                            border: isDuplicate
                                              ? '2px solid rgba(211, 47, 47, 0.35)'
                                              : '1px solid rgba(0,0,0,0.1)',
                                            backgroundColor: isDuplicate
                                              ? 'rgba(211, 47, 47, 0.06)'
                                              : tripDisplayStatus === TripLegStatus.completed ||
                                                  tripDisplayStatus === TripLegStatus.arrived
                                                ? '#ffffff'
                                                : 'rgba(158, 158, 158, 0.06)',
                                            boxShadow: '0 2px 6px rgba(0,0,0,0.06)'
                                          }}>
                                          <Stack spacing={1} alignItems="center">
                                            {/* Manifest ID */}
                                            <Typography
                                              variant="body2"
                                              sx={{
                                                fontWeight: 700,
                                                fontSize: '0.875rem',
                                                lineHeight: 1.2,
                                                color: isDuplicate ? '#c62828' : 'text.primary',
                                                textAlign: 'center'
                                              }}>
                                              {manifest.id}
                                            </Typography>

                                            {/* Status Badge */}
                                            <Chip
                                              label={tripDisplayStatus}
                                              size="small"
                                              sx={{
                                                height: 20,
                                                fontSize: '0.65rem',
                                                fontWeight: 600,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.3px',
                                                backgroundColor:
                                                  tripDisplayStatus === TripLegStatus.completed ||
                                                  tripDisplayStatus === TripLegStatus.arrived
                                                    ? 'rgba(76, 175, 80, 0.15)'
                                                    : 'rgba(158, 158, 158, 0.15)',
                                                color:
                                                  tripDisplayStatus === TripLegStatus.completed ||
                                                  tripDisplayStatus === TripLegStatus.arrived
                                                    ? '#388e3c'
                                                    : '#616161',
                                                border: `1px solid ${
                                                  tripDisplayStatus === TripLegStatus.completed ||
                                                  tripDisplayStatus === TripLegStatus.arrived
                                                    ? 'rgba(76, 175, 80, 0.3)'
                                                    : 'rgba(158, 158, 158, 0.3)'
                                                }`,
                                                '& .MuiChip-label': {
                                                  px: 1
                                                }
                                              }}
                                            />
                                          </Stack>
                                        </Box>
                                      );
                                    })
                                  )}
                                </Stack>
                              </Box>
                            ))}
                          </Stack>
                        </Box>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails
                      sx={{
                        px: 3,
                        py: 2.5,
                        backgroundColor: 'rgba(0, 0, 0, 0.01)',
                        borderTop: '1px solid rgba(0,0,0,0.08)'
                      }}>
                      {trip.legs.reduce((sum, leg) => sum + leg.manifests.length, 0) === 0 ? (
                        <Box
                          sx={{
                            textAlign: 'center',
                            py: 5,
                            px: 3,
                            borderRadius: 2,
                            border: '1px dashed rgba(0,0,0,0.12)'
                          }}>
                          <LocalShippingIcon sx={{ fontSize: 48, color: 'rgba(0,0,0,0.26)', mb: 2 }} />
                          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5, color: 'text.primary' }}>
                            No Legs
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            This trip doesn't have any legs associated with it
                          </Typography>
                        </Box>
                      ) : (
                        <Box>
                          {trip.legs.map((leg, legIndex) => (
                            <Box key={`${trip.id}-${leg.id}-${legIndex}`}>
                              {legIndex > 0 && (
                                <Divider
                                  sx={{
                                    my: 4,
                                    borderColor: 'rgba(0,0,0,0.12)'
                                  }}
                                />
                              )}
                              <Box
                                sx={{
                                  mb: 3,
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                  border: '1px solid rgba(0,0,0,0.06)',
                                  borderRadius: '8px !important',
                                  backgroundColor: 'rgba(25, 118, 210, 0.02)'
                                }}>
                                <Box
                                  sx={{
                                    mb: 2,
                                    px: 3,
                                    py: 1.5,
                                    backgroundColor: 'rgba(25, 118, 210, 0.06)',
                                    borderRadius: 2,
                                    border: '1px solid rgba(25, 118, 210, 0.15)',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
                                  }}>
                                  <Stack
                                    direction="row"
                                    spacing={1.5}
                                    alignItems="center"
                                    justifyContent="space-between">
                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                      <Typography
                                        sx={{
                                          fontSize: '0.8rem',
                                          fontWeight: 700,
                                          color: '#1976d2',
                                          textTransform: 'uppercase',
                                          letterSpacing: '0.5px'
                                        }}>
                                        Leg {legIndex + 1}
                                      </Typography>
                                      <Typography sx={{ color: 'rgba(0,0,0,0.3)', fontWeight: 500 }}>|</Typography>
                                      <Typography
                                        sx={{
                                          fontSize: '0.875rem',
                                          fontWeight: 600,
                                          color: 'text.primary'
                                        }}>
                                        {leg.origin?.businessName || 'Unknown'}
                                      </Typography>
                                      <Typography sx={{ color: 'rgba(25, 118, 210, 0.6)', fontSize: '0.9rem' }}>
                                        →
                                      </Typography>
                                      <Typography
                                        sx={{
                                          fontSize: '0.875rem',
                                          fontWeight: 600,
                                          color: 'text.primary'
                                        }}>
                                        {leg.destination?.businessName || 'Unknown'}
                                      </Typography>
                                    </Stack>
                                    <Stack direction="row" spacing={2} alignItems="center">
                                      {leg.departureDate && (
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                          <Typography
                                            sx={{
                                              fontSize: '0.7rem',
                                              fontWeight: 600,
                                              color: 'rgba(25, 118, 210, 0.7)',
                                              textTransform: 'uppercase',
                                              letterSpacing: '0.3px'
                                            }}>
                                            Departure:
                                          </Typography>
                                          <Typography
                                            sx={{
                                              fontSize: '0.75rem',
                                              fontWeight: 700,
                                              color: '#1976d2'
                                            }}>
                                            {dayjs(leg.departureDate).format(dateFormat)}
                                          </Typography>
                                        </Stack>
                                      )}
                                      {leg.arrivalDate && (
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                          <Typography
                                            sx={{
                                              fontSize: '0.7rem',
                                              fontWeight: 600,
                                              color: 'rgba(76, 175, 80, 0.7)',
                                              textTransform: 'uppercase',
                                              letterSpacing: '0.3px'
                                            }}>
                                            Arrival:
                                          </Typography>
                                          <Typography
                                            sx={{
                                              fontSize: '0.75rem',
                                              fontWeight: 700,
                                              color: '#388e3c'
                                            }}>
                                            {dayjs(leg.arrivalDate).format(dateFormat)}
                                          </Typography>
                                        </Stack>
                                      )}
                                    </Stack>
                                  </Stack>
                                </Box>

                                {leg.manifests.length === 0 ? (
                                  <Box
                                    sx={{
                                      py: 1.5,
                                      px: 2,
                                      mx: 2,
                                      mb: 2,
                                      borderRadius: 1,
                                      backgroundColor: 'rgba(0,0,0,0.02)',
                                      border: '1px solid rgba(0,0,0,0.08)'
                                    }}>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      fontStyle="italic"
                                      fontSize="0.85rem">
                                      No manifests for this leg
                                    </Typography>
                                  </Box>
                                ) : (
                                  leg.manifests.map((manifest, manifestIndex) => {
                                    const transformedManifest = transformManifest({ manifest, trip, leg });
                                    const manifestDuplicateOccurrences = manifestOccurrences.get(manifest.id) ?? [];
                                    const isDuplicateManifest = manifestDuplicateOccurrences.length > 1;

                                    const tripDisplayStatus = getTripDisplayStatus({
                                      manifestStatus: manifest.status,
                                      legStatus: leg.status
                                    });

                                    return (
                                      <Box key={`${legIndex}-${manifest.id}-${manifestIndex}`} sx={{ px: 3 }}>
                                        <Box
                                          sx={{
                                            mb: 3,
                                            borderRadius: 2,
                                            backgroundColor: 'background.default',
                                            border: isDuplicateManifest
                                              ? '2px solid #d32f2f'
                                              : '1px solid rgba(0,0,0,0.12)',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                                          }}>
                                          {/* Duplicate Warning */}
                                          {isDuplicateManifest && (
                                            <Box
                                              sx={{
                                                backgroundColor: 'rgba(211, 47, 47, 0.06)',
                                                borderBottom: '1px solid rgba(211, 47, 47, 0.2)',
                                                px: 2.5,
                                                py: 1.5,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1.5
                                              }}>
                                              <ErrorOutlineIcon
                                                sx={{
                                                  fontSize: 18,
                                                  color: '#c62828'
                                                }}
                                              />
                                              <Stack spacing={1}>
                                                <Typography
                                                  sx={{
                                                    fontSize: '0.75rem',
                                                    fontWeight: 700,
                                                    color: '#c62828',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.5px'
                                                  }}>
                                                  Duplicate Detected • {manifestDuplicateOccurrences.length} occurrences
                                                </Typography>
                                                <Stack
                                                  direction="row"
                                                  spacing={0.75}
                                                  alignItems="center"
                                                  flexWrap="wrap">
                                                  {manifestDuplicateOccurrences.map((info, manifestDupeIndex) => (
                                                    <Chip
                                                      key={`${info.tripId}-${info.legIndex}-${manifestDupeIndex}`}
                                                      label={`Trip ${info.tripId || 'N/A'} • Leg ${
                                                        Number.isInteger(info.legIndex) ? info.legIndex + 1 : 'N/A'
                                                      }`}
                                                      size="small"
                                                      sx={{
                                                        height: 18,
                                                        fontSize: '0.65rem',
                                                        fontWeight: 600,
                                                        backgroundColor: 'rgba(211, 47, 47, 0.1)',
                                                        color: '#b71c1c',
                                                        border: '1px solid rgba(211, 47, 47, 0.25)',
                                                        '& .MuiChip-label': {
                                                          px: 1
                                                        }
                                                      }}
                                                    />
                                                  ))}
                                                </Stack>
                                              </Stack>
                                            </Box>
                                          )}

                                          {/* Manifest Header */}
                                          <Box
                                            sx={{
                                              px: 3,
                                              py: 2.5,
                                              borderLeft: '1px solid #42a5f5',
                                              borderRight: '1px solid #42a5f5',
                                              borderBottom: '1px solid rgba(0,0,0,0.08)',
                                              position: 'relative',
                                              borderTopLeftRadius: 8,
                                              borderTopRightRadius: 8,
                                              overflow: 'hidden'
                                            }}>
                                            <Box
                                              sx={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                width: '100%',
                                                height: 6,
                                                background: '#1976d2',
                                                borderTopLeftRadius: 8,
                                                borderTopRightRadius: 8
                                              }}
                                            />
                                            <Stack spacing={2} position="relative" zIndex={1}>
                                              <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                <Stack direction="row" spacing={2} alignItems="center">
                                                  <Box
                                                    sx={{
                                                      width: 40,
                                                      height: 40,
                                                      borderRadius: '50%',
                                                      backgroundColor: 'rgba(25, 118, 210, 0.1)',
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      justifyContent: 'center'
                                                    }}>
                                                    <InventoryIcon
                                                      sx={{
                                                        fontSize: 20,
                                                        color: '#1976d2'
                                                      }}
                                                    />
                                                  </Box>
                                                  <Box>
                                                    <Typography
                                                      variant="h6"
                                                      sx={{
                                                        fontWeight: 700,
                                                        fontSize: '1.1rem',
                                                        letterSpacing: '-0.3px',
                                                        color: 'text.primary'
                                                      }}>
                                                      {manifest.id}
                                                    </Typography>
                                                    <Typography
                                                      variant="caption"
                                                      sx={{
                                                        color: 'text.secondary',
                                                        fontSize: '0.75rem',
                                                        fontWeight: 500
                                                      }}>
                                                      Trailer: {manifest.trailerId || 'N/A'}
                                                    </Typography>
                                                  </Box>
                                                </Stack>
                                                <Chip
                                                  label={tripDisplayStatus}
                                                  size="small"
                                                  sx={{
                                                    height: 26,
                                                    fontSize: '0.75rem',
                                                    fontWeight: 600,
                                                    textTransform: 'capitalize',
                                                    backgroundColor:
                                                      tripDisplayStatus === TripLegStatus.completed ||
                                                      tripDisplayStatus === TripLegStatus.arrived
                                                        ? 'rgba(76, 175, 80, 0.15)'
                                                        : 'rgba(158, 158, 158, 0.15)',
                                                    color:
                                                      tripDisplayStatus === TripLegStatus.completed ||
                                                      tripDisplayStatus === TripLegStatus.arrived
                                                        ? '#388e3c'
                                                        : '#616161',
                                                    border: `1px solid ${
                                                      tripDisplayStatus === TripLegStatus.completed ||
                                                      tripDisplayStatus === TripLegStatus.arrived
                                                        ? 'rgba(76, 175, 80, 0.3)'
                                                        : 'rgba(158, 158, 158, 0.3)'
                                                    }`
                                                  }}
                                                />
                                              </Stack>
                                            </Stack>
                                          </Box>

                                          {/* Manifest Content (unchanged) */}
                                          <Box
                                            sx={{
                                              p: 3,
                                              backgroundColor: '#f5f7fa',
                                              borderLeft: '1px solid #42a5f5',
                                              borderRight: '1px solid #42a5f5',
                                              borderBottom: '1px solid #42a5f5',
                                              borderBottomLeftRadius: 8,
                                              borderBottomRightRadius: 8
                                            }}>
                                            {/* SSCCs & ASNs (paired per SSCC) */}
                                            <Box>
                                              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                                                <InventoryIcon
                                                  sx={{
                                                    fontSize: 16,
                                                    color: 'text.secondary'
                                                  }}
                                                />
                                                <Typography variant="body2" fontWeight={600} color="text.primary">
                                                  SSCCs & ASNs
                                                </Typography>
                                                <Chip
                                                  label={transformedManifest.ssccsWithAsns.length}
                                                  size="small"
                                                  sx={{
                                                    height: 18,
                                                    fontSize: '0.7rem',
                                                    backgroundColor: 'rgba(0,0,0,0.06)',
                                                    color: 'text.secondary',
                                                    fontWeight: 600
                                                  }}
                                                />
                                              </Stack>

                                              <Stack spacing={1}>
                                                {transformedManifest.ssccsWithAsns.map((ssccWithAsns, ssccIndex) => {
                                                  const ssccDuplicateInfo =
                                                    ssccOccurrences.get(ssccWithAsns.ssccId) ?? [];
                                                  const isSsccDuplicate = ssccDuplicateInfo?.length > 1;

                                                  return (
                                                    <Box
                                                      key={`${ssccWithAsns.ssccId}-${ssccIndex}`}
                                                      onClick={() => {
                                                        setSsccActionsPayload({
                                                          tripId: trip.id,
                                                          legId: leg.id,
                                                          manifestId: manifest.id,
                                                          ssccId: ssccWithAsns.ssccId,
                                                          sscc: ssccs.find((s) => s.id === ssccWithAsns.ssccId)
                                                        });
                                                        setSsccActionsOpen(true);
                                                      }}
                                                      sx={{
                                                        mb: 3,
                                                        borderRadius: 2,
                                                        backgroundColor: 'background.default',
                                                        border: isSsccDuplicate
                                                          ? '2px solid #d32f2f'
                                                          : '1px solid rgba(0,0,0,0.12)',
                                                        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                                                        cursor: 'pointer',
                                                        transition: 'box-shadow 0.2s ease',
                                                        '&:hover': {
                                                          boxShadow: '0 3px 10px rgba(0,0,0,0.12)'
                                                        }
                                                      }}>
                                                      {isSsccDuplicate && (
                                                        <Box
                                                          sx={{
                                                            backgroundColor: 'rgba(211, 47, 47, 0.06)',
                                                            borderBottom: '1px solid rgba(211, 47, 47, 0.2)',
                                                            px: 2.5,
                                                            py: 1.5,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1.5
                                                          }}>
                                                          <ErrorOutlineIcon
                                                            sx={{
                                                              fontSize: 18,
                                                              color: '#c62828'
                                                            }}
                                                          />
                                                          <Stack spacing={1}>
                                                            <Typography
                                                              sx={{
                                                                fontSize: '0.75rem',
                                                                fontWeight: 700,
                                                                color: '#c62828',
                                                                textTransform: 'uppercase',
                                                                letterSpacing: '0.5px'
                                                              }}>
                                                              Duplicate Detected • {ssccDuplicateInfo.length}{' '}
                                                              occurrences
                                                            </Typography>
                                                            <Stack
                                                              direction="row"
                                                              spacing={0.75}
                                                              alignItems="center"
                                                              flexWrap="wrap">
                                                              {ssccDuplicateInfo.map((info, ssccDuplicateInfoIndex) => (
                                                                <Chip
                                                                  key={ssccDuplicateInfoIndex}
                                                                  label={`Trip ${info.tripId || 'N/A'} • Leg ${
                                                                    Number.isInteger(info.legIndex)
                                                                      ? (info.legIndex ?? 0) + 1
                                                                      : 'N/A'
                                                                  } • Manifest ${info.manifestId || 'N/A'}`}
                                                                  size="small"
                                                                  sx={{
                                                                    height: 18,
                                                                    fontSize: '0.65rem',
                                                                    fontWeight: 600,
                                                                    backgroundColor: 'rgba(211, 47, 47, 0.1)',
                                                                    color: '#b71c1c',
                                                                    border: '1px solid rgba(211, 47, 47, 0.25)',
                                                                    '& .MuiChip-label': {
                                                                      px: 1
                                                                    }
                                                                  }}
                                                                />
                                                              ))}
                                                            </Stack>
                                                          </Stack>
                                                        </Box>
                                                      )}
                                                      <Box
                                                        sx={{
                                                          display: 'flex',
                                                          alignItems: 'center',
                                                          gap: 2,
                                                          p: 1.5,
                                                          backgroundColor: 'rgba(15, 23, 42, 0.02)',
                                                          borderRadius: 1.5,
                                                          border: '1px solid rgba(0,0,0,0.06)'
                                                        }}>
                                                        <Box
                                                          sx={{
                                                            flex: '1 1 auto',
                                                            display: 'grid',
                                                            gridTemplateColumns: '1fr 1fr',
                                                            gap: 2
                                                          }}>
                                                          {/* SSCC */}
                                                          <Stack direction="column" spacing={0.75}>
                                                            <Typography
                                                              variant="caption"
                                                              sx={{
                                                                fontWeight: 600,
                                                                color: 'text.secondary',
                                                                my: 1
                                                              }}>
                                                              SSCC
                                                            </Typography>
                                                            <Stack
                                                              direction="row"
                                                              spacing={1}
                                                              alignItems="center"
                                                              flexWrap="wrap">
                                                              <Typography
                                                                variant="body2"
                                                                sx={{
                                                                  fontSize: '0.85rem',
                                                                  fontWeight: '600'
                                                                }}>
                                                                {ssccWithAsns.ssccId}
                                                              </Typography>
                                                              <Chip
                                                                label={ssccWithAsns.sscc?.status || 'Unknown Status'}
                                                                size="small"
                                                                sx={{
                                                                  height: 20,
                                                                  fontSize: '0.7rem',
                                                                  fontWeight: 600,
                                                                  backgroundColor: 'rgba(0,0,0,0.06)',
                                                                  color: 'text.secondary'
                                                                }}
                                                              />

                                                              {ssccWithAsns.sscc?.status ? (
                                                                <Typography
                                                                  variant="caption"
                                                                  sx={{
                                                                    fontSize: '0.75rem',
                                                                    color: 'text.secondary'
                                                                  }}>
                                                                  {getTimeBasedOnStatus({
                                                                    status: ssccWithAsns.sscc?.status as SsccStatus,
                                                                    sscc: ssccWithAsns.sscc
                                                                  })}
                                                                </Typography>
                                                              ) : null}
                                                            </Stack>
                                                            <Typography
                                                              variant="caption"
                                                              sx={{
                                                                color: 'text.secondary',
                                                                fontSize: '0.75rem'
                                                              }}>
                                                              Nickname: {ssccWithAsns.sscc?.nickname || 'N/A'}
                                                            </Typography>
                                                          </Stack>

                                                          {/* ASNs for this SSCC */}
                                                          <Stack direction="column">
                                                            <Typography
                                                              variant="caption"
                                                              sx={{
                                                                fontWeight: 600,
                                                                color: 'text.secondary',
                                                                my: 1
                                                              }}>
                                                              ASNs
                                                            </Typography>

                                                            {ssccWithAsns.asns.length === 0 ? (
                                                              <Typography
                                                                variant="body2"
                                                                color="text.secondary"
                                                                fontStyle="italic">
                                                                No ASNs
                                                              </Typography>
                                                            ) : (
                                                              <Stack spacing={0.25}>
                                                                {ssccWithAsns.asns.map((asn, asnIndex) => {
                                                                  const asnKey = buildAsnKey(asn);
                                                                  const asnDuplicateInfo = asnKey
                                                                    ? (asnOccurrences.get(asnKey) ?? [])
                                                                    : [];
                                                                  const isAsnDuplicate = asnDuplicateInfo.length > 1;

                                                                  return (
                                                                    <Box
                                                                      key={`${asn.asn}-${asnIndex}`}
                                                                      sx={{
                                                                        mb: 3,
                                                                        borderRadius: 2,
                                                                        backgroundColor: 'background.default',
                                                                        border: isAsnDuplicate
                                                                          ? '2px solid #d32f2f'
                                                                          : 'none',
                                                                        boxShadow: isAsnDuplicate
                                                                          ? '0 1px 3px rgba(0,0,0,0.08)'
                                                                          : 'none'
                                                                      }}>
                                                                      {isAsnDuplicate && (
                                                                        <Box
                                                                          sx={{
                                                                            backgroundColor: 'rgba(211, 47, 47, 0.06)',
                                                                            borderBottom:
                                                                              '1px solid rgba(211, 47, 47, 0.2)',
                                                                            px: 2.5,
                                                                            py: 1.5,
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: 1.5
                                                                          }}>
                                                                          <ErrorOutlineIcon
                                                                            sx={{
                                                                              fontSize: 18,
                                                                              color: '#c62828'
                                                                            }}
                                                                          />
                                                                          <Stack spacing={1}>
                                                                            <Typography
                                                                              sx={{
                                                                                fontSize: '0.75rem',
                                                                                fontWeight: 700,
                                                                                color: '#c62828',
                                                                                textTransform: 'uppercase',
                                                                                letterSpacing: '0.5px'
                                                                              }}>
                                                                              Duplicate Detected •{' '}
                                                                              {asnDuplicateInfo.length} occurrences
                                                                            </Typography>
                                                                            <Stack
                                                                              direction="row"
                                                                              spacing={0.75}
                                                                              alignItems="center"
                                                                              flexWrap="wrap">
                                                                              {asnDuplicateInfo.map(
                                                                                (info, asnDuplicateInfoIndex) => (
                                                                                  <Chip
                                                                                    key={`${info.tripId}-${info.legIndex}-${info.manifestId}-${asnDuplicateInfoIndex}`}
                                                                                    label={`Trip ${info.tripId || 'N/A'} • Leg ${
                                                                                      Number.isInteger(info.legIndex)
                                                                                        ? (info.legIndex ?? 0) + 1
                                                                                        : 'N/A'
                                                                                    } • Manifest ${info.manifestId || 'N/A'} • SSCC ${
                                                                                      info.ssccId || 'N/A'
                                                                                    }`}
                                                                                    size="small"
                                                                                    sx={{
                                                                                      height: 18,
                                                                                      fontSize: '0.65rem',
                                                                                      fontWeight: 600,
                                                                                      backgroundColor:
                                                                                        'rgba(211, 47, 47, 0.1)',
                                                                                      color: '#b71c1c',
                                                                                      border:
                                                                                        '1px solid rgba(211, 47, 47, 0.25)',
                                                                                      '& .MuiChip-label': {
                                                                                        px: 1
                                                                                      }
                                                                                    }}
                                                                                  />
                                                                                )
                                                                              )}
                                                                            </Stack>
                                                                          </Stack>
                                                                        </Box>
                                                                      )}

                                                                      <Box
                                                                        sx={{
                                                                          px: 1.5,
                                                                          py: 0.75,
                                                                          borderRadius: 1,
                                                                          backgroundColor: 'rgba(0,0,0,0.03)',
                                                                          border: '1px solid rgba(0,0,0,0.06)'
                                                                        }}>
                                                                        <Stack
                                                                          direction="row"
                                                                          alignItems="center"
                                                                          flexWrap="wrap"
                                                                          sx={{ gap: 0.75 }}>
                                                                          <Typography
                                                                            key={asn.asn}
                                                                            variant="body2"
                                                                            sx={{
                                                                              fontSize: '0.85rem',
                                                                              fontWeight: '600'
                                                                            }}>
                                                                            {asn.asn}
                                                                          </Typography>

                                                                          <Chip
                                                                            label={asn.status || 'Unknown Status'}
                                                                            size="small"
                                                                            sx={{
                                                                              height: 20,
                                                                              fontSize: '0.7rem',
                                                                              fontWeight: 600,
                                                                              backgroundColor: 'rgba(0,0,0,0.06)',
                                                                              color: 'text.secondary'
                                                                            }}
                                                                          />
                                                                        </Stack>
                                                                      </Box>
                                                                    </Box>
                                                                  );
                                                                })}
                                                              </Stack>
                                                            )}
                                                          </Stack>
                                                        </Box>
                                                        <Box
                                                          sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            color: 'text.secondary'
                                                          }}>
                                                          <ChevronRightIcon sx={{ fontSize: 20 }} />
                                                        </Box>
                                                      </Box>
                                                    </Box>
                                                  );
                                                })}
                                              </Stack>
                                            </Box>
                                            <Divider
                                              sx={{
                                                borderColor: 'rgba(0,0,0,0.08)',
                                                my: 2
                                              }}
                                            />

                                            <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                                              <Tooltip
                                                title={
                                                  ![TripLegStatus.arrived, TripLegStatus.completed].includes(
                                                    tripDisplayStatus
                                                  )
                                                    ? `Trip status is ${tripDisplayStatus}, please add SSCC in Bustle.`
                                                    : ''
                                                }
                                                arrow>
                                                <span>
                                                  <Button
                                                    variant="outlined"
                                                    size="small"
                                                    disabled={
                                                      ![TripLegStatus.arrived, TripLegStatus.completed].includes(
                                                        tripDisplayStatus
                                                      )
                                                    }
                                                    startIcon={
                                                      <AddIcon
                                                        sx={{
                                                          fontSize: 16
                                                        }}
                                                      />
                                                    }
                                                    onClick={() => {
                                                      const ssccStatus =
                                                        mapTripLegStatusToSsccStatus(tripDisplayStatus);

                                                      const asnStatus = mapSsccStatusToAsnStatus({
                                                        ssccStatus,
                                                        manifestStatus: manifest.status
                                                      });

                                                      handleOpenAddSSCCToManifestDialog({
                                                        manifest: transformedManifest,
                                                        tripDisplayStatus,
                                                        ssccStatus,
                                                        asnStatus,
                                                        tripId: trip.id,
                                                        legId: leg.id
                                                      });
                                                    }}
                                                    sx={{
                                                      minWidth: 180,
                                                      px: 2,
                                                      py: 0.75,
                                                      height: 36,
                                                      fontWeight: 600,
                                                      fontSize: '0.8rem',
                                                      textTransform: 'none',
                                                      borderColor: 'rgba(25, 118, 210, 0.6)',
                                                      color: '#1976d2',
                                                      borderRadius: 1.5,
                                                      '&:hover': {
                                                        borderColor: '#1976d2',
                                                        backgroundColor: 'rgba(25, 118, 210, 0.04)'
                                                      }
                                                    }}>
                                                    Add SSCC To Manifest
                                                  </Button>
                                                </span>
                                              </Tooltip>
                                              <Tooltip
                                                title={
                                                  ![TripLegStatus.arrived, TripLegStatus.completed].includes(
                                                    tripDisplayStatus
                                                  )
                                                    ? `Trip status is ${tripDisplayStatus}, you can not mark completed.`
                                                    : ''
                                                }
                                                arrow>
                                                <span>
                                                  <Button
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={
                                                      <CheckCircleIcon
                                                        sx={{
                                                          fontSize: 16
                                                        }}
                                                      />
                                                    }
                                                    onClick={() =>
                                                      handleOpenMarkCompletedManifestDialog({
                                                        tripDisplayStatus,
                                                        transformedManifest,
                                                        manifestStatus: manifest.status,
                                                        tripId: trip.id,
                                                        legId: leg.id
                                                      })
                                                    }
                                                    disabled={
                                                      ![TripLegStatus.arrived, TripLegStatus.completed].includes(
                                                        tripDisplayStatus
                                                      )
                                                    }
                                                    sx={{
                                                      minWidth: 200,
                                                      px: 2,
                                                      py: 0.75,
                                                      height: 36,
                                                      fontWeight: 600,
                                                      fontSize: '0.8rem',
                                                      textTransform: 'none',
                                                      borderColor: 'rgba(76, 175, 80, 0.6)',
                                                      color: '#4caf50',
                                                      borderRadius: 1.5,
                                                      '&:hover': {
                                                        borderColor: '#4caf50',
                                                        backgroundColor: 'rgba(76, 175, 80, 0.04)'
                                                      }
                                                    }}>
                                                    Mark Manifest Completed
                                                  </Button>
                                                </span>
                                              </Tooltip>
                                              <Tooltip
                                                title={
                                                  ![TripLegStatus.arrived, TripLegStatus.completed].includes(
                                                    tripDisplayStatus
                                                  )
                                                    ? `Trip status is ${tripDisplayStatus}, please delete manifest in Bustle.`
                                                    : ''
                                                }
                                                arrow>
                                                <span>
                                                  <Button
                                                    variant="outlined"
                                                    size="small"
                                                    disabled={
                                                      ![TripLegStatus.arrived, TripLegStatus.completed].includes(
                                                        tripDisplayStatus
                                                      )
                                                    }
                                                    startIcon={
                                                      <DeleteIcon
                                                        sx={{
                                                          fontSize: 16
                                                        }}
                                                      />
                                                    }
                                                    onClick={() => {
                                                      handleOpenDeleteManifestDialog({
                                                        tripDisplayStatus,
                                                        manifestStatus: manifest.status,
                                                        transformedManifest,
                                                        tripId: trip.id,
                                                        legId: leg.id
                                                      });
                                                    }}
                                                    sx={{
                                                      minWidth: 160,
                                                      px: 2,
                                                      py: 0.75,
                                                      height: 36,
                                                      fontWeight: 600,
                                                      fontSize: '0.8rem',
                                                      textTransform: 'none',
                                                      borderColor: 'rgba(211, 47, 47, 0.6)',
                                                      color: '#c62828',
                                                      borderRadius: 1.5,
                                                      '&:hover': {
                                                        borderColor: '#c62828',
                                                        backgroundColor: 'rgba(211, 47, 47, 0.06)'
                                                      }
                                                    }}>
                                                    Delete Manifest
                                                  </Button>
                                                </span>
                                              </Tooltip>
                                            </Stack>
                                          </Box>
                                        </Box>
                                      </Box>
                                    );
                                  })
                                )}
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </AccordionDetails>
                  </Accordion>
                );
              })}

              {/* Pagination */}

              <Box sx={{ height: '3rem' }} />

              <Box
                sx={{
                  position: 'fixed',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  zIndex: 10,
                  backgroundColor: 'background.default',
                  borderTop: '1px solid rgba(0,0,0,0.08)',
                  py: 1.5
                }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 3 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                    Showing {Math.min(currentPage * PAGE_SIZE, filteredTripSheets.length)} of{' '}
                    {filteredTripSheets.length} trip sheets
                  </Typography>
                  <Pagination count={totalPages} page={currentPage} onChange={handlePageChange} color="primary" />
                </Stack>
              </Box>
            </Stack>
          )}
        </Box>
      </Box>

      {/* 00 SSCC Actions Drawer */}
      {ssccActionsOpen && (ssccActionsContext || ssccActionsPayload) && (
        <SsccActionsDrawer
          open={ssccActionsOpen}
          onClose={handleCloseSsccActions}
          ssccs={ssccs}
          ssccActionsContext={ssccActionsContext}
          ssccActionsPayload={ssccActionsPayload}
          asnOccurrences={asnOccurrences}
          ssccOccurrences={ssccOccurrences}
          handleOpenAddASNToSSCCDialog={handleOpenAddASNToSSCCDialog}
          handleOpenMarkCompletedSSCCDialog={handleOpenMarkCompletedSSCCDialog}
          handleOpenDeleteSSCCDialog={handleOpenDeleteSSCCDialog}
          handleOpenDeleteASNDialog={handleOpenDeleteASNDialog}
          getTimeBasedOnStatus={getTimeBasedOnStatus}
        />
      )}

      {/* 01 Add SSCC to Manifest Dialog */}
      {openAddSSCCToManifestDialog && addSSCCToManifestData && (
        <AddSSCCToManifestDialog
          open={openAddSSCCToManifestDialog}
          selectedManifest={addSSCCToManifestData}
          availableSSCCs={availableSSCCs}
          selectedSSCCId={addSSCCToManifestSelectedId}
          ssccWeight={addSSCCToManifestWeight}
          actionReason={addSSCCToManifestReason}
          withReceiveManifest={addSSCCToManifestReceiveManifest}
          isProcessing={isProcessing}
          onClose={resetAddSSCCToManifestState}
          onSSCCIdChange={setAddSSCCToManifestSelectedId}
          onWeightChange={setAddSSCCToManifestWeight}
          onReasonChange={setAddSSCCToManifestReason}
          onReceiveManifestChange={setAddSSCCToManifestReceiveManifest}
          onConfirm={handleAddSSCCToManifest}
          onAlert={alert}
        />
      )}

      {/* 02 Mark as Completed Manifest Dialog */}
      {openCompleteManifestDialog && completeManifestData && (
        <MarkCompletedManifestDialog
          open={openCompleteManifestDialog}
          selectedManifest={completeManifestData}
          actionReason={completeManifestReason}
          isProcessing={isProcessing}
          onClose={resetCompleteManifestState}
          onReasonChange={setCompleteManifestReason}
          onConfirm={handleMarkAsCompletedManifest}
        />
      )}

      {/* 03 Delete Manifest Dialog */}
      {openDeleteManifestDialog && deleteManifestData && (
        <DeleteManifestDialog
          open={openDeleteManifestDialog}
          selectedManifest={deleteManifestData}
          actionReason={deleteManifestReason}
          isProcessing={isProcessing}
          onClose={resetDeleteManifestState}
          onReasonChange={setDeleteManifestReason}
          onConfirm={handleDeleteManifest}
        />
      )}

      {/* 04A Add ASN to SSCC Dialog */}
      {openAddASNToSSCCDialog && addASNToSSCCData && (
        <AddASNToSSCCDialog
          open={openAddASNToSSCCDialog}
          selectedManifest={addASNToSSCCData}
          ssccId={addASNToSSCCData?.ssccId || ''}
          availableASNs={availableASNsForAssignedSSCC}
          selectedASNId={addASNToSSCCSelectedId}
          selectedReceiptEvent={addASNToSSCCReceiptEvent}
          receiptedEvents={addASNToSSCCData?.sscc?.sscc?.receiptedEvents || []}
          actionReason={addASNToSSCCReason}
          withReceiveManifest={addASNToSSCCReceiveManifest}
          isProcessing={isProcessing}
          onClose={resetAddASNToSSCCState}
          onASNIdChange={setAddASNToSSCCSelectedId}
          onReceiptEventChange={setAddASNToSSCCReceiptEvent}
          onReasonChange={setAddASNToSSCCReason}
          onReceiveManifestChange={setAddASNToSSCCReceiveManifest}
          onConfirm={() => {
            handleAddASNToSSCC({
              selectedManifestSSCC: addASNToSSCCData,
              tripSheets,
              asn: availableASNsForAssignedSSCC.find((asn) => asn.asn === addASNToSSCCSelectedId) || null,
              ssccId: addASNToSSCCData.ssccId || '',
              actionReason: addASNToSSCCReason,
              tripId: addASNToSSCCData?.tripId,
              legId: addASNToSSCCData?.legId,
              manifestId: addASNToSSCCData?.manifest?.id,
              asnStatus:
                availableASNsForAssignedSSCC.find((asn) => asn.asn === addASNToSSCCSelectedId)?.status ===
                AsnStatus.available
                  ? AsnStatus.available
                  : addASNToSSCCData.asnStatus,
              receiptedEventIndex: addASNToSSCCData?.sscc?.sscc?.receiptedEvents?.findIndex(
                (event, index) => `${index + 1}. ${event.name} - ${event.site}` === addASNToSSCCReceiptEvent
              ),
              withReceiveManifestCheck: addASNToSSCCData?.manifest?.status === 'Completed'
            });
          }}
        />
      )}

      {/* 04B Add ASN to Unassigned SSCC Dialog */}
      {openAddASNToSSCCDialog && addASNToUnassignedSSCCData && (
        <AddASNForUnassignedSSCCDialog
          open={openAddASNToSSCCDialog}
          ssccId={addASNToUnassignedSSCCData?.ssccId || ''}
          asnStatus={addASNToUnassignedSSCCData?.ssccStatus || ''}
          availableASNs={availableASNsForUnassignedSSCC}
          selectedASNId={addASNToSSCCSelectedId}
          selectedReceiptEvent={addASNToSSCCReceiptEvent}
          receiptedEvents={addASNToUnassignedSSCCData?.sscc?.receiptedEvents || []}
          actionReason={addASNToSSCCReason}
          isProcessing={isProcessing}
          onClose={resetAddASNToSSCCState}
          onASNIdChange={setAddASNToSSCCSelectedId}
          onReceiptEventChange={setAddASNToSSCCReceiptEvent}
          onReasonChange={setAddASNToSSCCReason}
          onConfirm={() => {
            handleAddASNToUnassignedSSCC({
              asn: availableASNsForUnassignedSSCC.find((asn) => asn.asn === addASNToSSCCSelectedId) || null,
              ssccId: addASNToUnassignedSSCCData?.ssccId || '',
              actionReason: addASNToSSCCReason,
              asnStatus:
                availableASNsForUnassignedSSCC.find((asn) => asn.asn === addASNToSSCCSelectedId)?.status ===
                AsnStatus.available
                  ? AsnStatus.available
                  : addASNToUnassignedSSCCData.ssccStatus,
              receiptedEventIndex: addASNToUnassignedSSCCData?.sscc?.receiptedEvents?.findIndex(
                (event, index) => `${index + 1}. ${event.name} - ${event.site}` === addASNToSSCCReceiptEvent
              )
            });
          }}
        />
      )}

      {/* 05A Mark as Completed SSCC Dialog */}
      {openCompleteSSCCDialog && completeSSCCData && (
        <MarkCompletedSSCCDialog
          open={openCompleteSSCCDialog}
          selectedManifest={completeSSCCData}
          ssccWeight={completeSSCCWeight}
          showWeightInput={!completeSSCCData?.sscc?.sscc?.weight}
          actionReason={completeSSCCReason}
          isProcessing={isProcessing}
          onClose={resetCompleteSSCCState}
          onWeightChange={setCompleteSSCCWeight}
          onReasonChange={setCompleteSSCCReason}
          onConfirm={handleMarkAsCompletedSSCC}
        />
      )}

      {/* 05B Mark as Completed Unassigned SSCC Dialog */}
      {openCompleteSSCCDialog && completeUnassignedSSCCData && (
        <MarkCompletedUnassignedSSCCDialog
          open={openCompleteSSCCDialog}
          ssccId={completeUnassignedSSCCData.ssccId}
          ssccStatus={completeUnassignedSSCCData.ssccStatus}
          ssccWeight={completeSSCCWeight}
          showWeightInput={!completeUnassignedSSCCData.sscc?.weight}
          actionReason={completeSSCCReason}
          isProcessing={isProcessing}
          onClose={resetCompleteSSCCState}
          onWeightChange={setCompleteSSCCWeight}
          onReasonChange={setCompleteSSCCReason}
          onConfirm={handleMarkAsCompletedUnassignedSSCC}
        />
      )}

      {/* 06 Delete SSCC Dialog */}
      {openDeleteSSCCDialog && deleteSSCCData && (
        <DeleteSSCCDialog
          open={openDeleteSSCCDialog}
          selectedManifest={deleteSSCCData}
          actionReason={deleteSSCCReason}
          isProcessing={isProcessing}
          onClose={resetDeleteSSCCState}
          onReasonChange={setDeleteSSCCReason}
          onConfirm={handleDeleteSSCC}
        />
      )}

      {/* 07 Delete ASN Dialog */}
      {openDeleteASNDialog && deleteASNData && (
        <DeleteASNDialog
          open={openDeleteASNDialog}
          selectedASN={deleteASNData}
          actionReason={deleteASNReason}
          selectedTransferSSCCId={deleteASNTransferSSCCId}
          selectedTransferReceiptedEvent={deleteASNTransferReceiptedEvent}
          availableTransferSSCCs={eligibleTransferSSCCs
            .filter((sscc) => sscc.id !== deleteASNData.ssccId)
            .sort((a, b) =>
              String(b.id).localeCompare(String(a.id), undefined, { numeric: true, sensitivity: 'base' })
            )}
          isProcessing={isProcessing}
          onClose={resetDeleteASNState}
          onReasonChange={setDeleteASNReason}
          onTransferSSCCIdChange={(ssccId) => {
            setDeleteASNTransferSSCCId(ssccId);
            setDeleteASNTransferReceiptedEvent('');
          }}
          onTransferReceiptedEventChange={setDeleteASNTransferReceiptedEvent}
          onConfirm={handleDeleteASN}
        />
      )}

      {/* Bustle Confirmation Dialog */}
      {openAdminBetaDialog && (
        <ConfirmationDialog
          open={openAdminBetaDialog}
          title="Beta Warning"
          message="This Admin Panel is in beta. Proceed with extra caution before performing any action."
          messageDetail="Actions in this panel can update live shipment and inventory records. Please review the selected item and action carefully before you submit."
          onClose={() => setOpenAdminBetaDialog(false)}
        />
      )}

      {openBustleDialog && (
        <ConfirmationDialog
          open={!!openBustleDialog}
          title="Action Required"
          message={openBustleDialog?.message}
          messageDetail={openBustleDialog?.messageDetail}
          onClose={() => setOpenBustleDialog(null)}
        />
      )}

      {/* Coupa Confirmation Dialog */}
      {openCoupaDialog && (
        <ConfirmationDialog
          open={openCoupaDialog}
          title="Action Required"
          message="Access Coupa and check Inventory Transfer"
          messageDetail="Please access Coupa to check already transferred inventory related to this deletion."
          onClose={() => setOpenCoupaDialog(false)}
        />
      )}
    </Stack>
  );
}
